import { useState, useEffect, useCallback, useRef } from "react";
import { auth, user as userApi, profiles as profilesApi, comics as comicsApi, lookup, importExport, admin } from "./api";

// ── SlabVault brand colors ────────────────────────────────────
const BRAND = {
  gold:    '#c9a84c',
  goldSoft:'#c9a84c22',
  red:     '#c0392b',
};

// ── Theme tokens ──────────────────────────────────────────────
const THEMES = {
  vault:     { name:"Vault",       bg:"#0e0e0e", surface:"#161616", surfaceAlt:"#1f1f1f", accent:"#c9a84c", accentSoft:"#c9a84c18", text:"#f0ede8", textMuted:"#7a7570", border:"#2a2a2a", icon:"🏛" },
  obsidian:  { name:"Obsidian",    bg:"#0a0c10", surface:"#12151c", surfaceAlt:"#1a1e28", accent:"#7b8cde", accentSoft:"#7b8cde18", text:"#e8eaf6", textMuted:"#5a6080", border:"#222840", icon:"🌑" },
  carbon:    { name:"Carbon",      bg:"#111111", surface:"#1a1a1a", surfaceAlt:"#222222", accent:"#e0e0e0", accentSoft:"#e0e0e018", text:"#f5f5f5", textMuted:"#666666", border:"#333333", icon:"⬛" },
  crimson:   { name:"Crimson",     bg:"#0d0a0a", surface:"#180f0f", surfaceAlt:"#211515", accent:"#c0392b", accentSoft:"#c0392b18", text:"#f5eeee", textMuted:"#7a5555", border:"#2e1a1a", icon:"🔴" },
  sterling:  { name:"Sterling",    bg:"#f4f4f2", surface:"#ffffff", surfaceAlt:"#eeeeec", accent:"#2c2c2c", accentSoft:"#2c2c2c12", text:"#1a1a1a", textMuted:"#777770", border:"#d8d8d4", icon:"⬜" },
};

// ── Root ──────────────────────────────────────────────────────
export default function App() {
  const [themeKey, setThemeKey]     = useState("vault");
  const [screen, setScreen]         = useState("loading");
  const [currentUser, setCurrentUser] = useState(null);
  const [profiles, setProfiles]     = useState([]);
  const [activeProfile, setActive]  = useState(null);
  const [notification, setNote]     = useState(null);
  const [showFinancials, setShowFinancials] = useState(() => {
    try { return localStorage.getItem('sv_show_financials') !== '0'; } catch { return true; }
  });

  const toggleShowFinancials = (val) => {
    setShowFinancials(val);
    try { localStorage.setItem('sv_show_financials', val ? '1' : '0'); } catch {}
  };

  const t = THEMES[themeKey] || THEMES['vault'];

  const notify = useCallback((msg, type = "info") => {
    setNote({ msg, type });
    setTimeout(() => setNote(null), 3500);
  }, []);

  // ── Boot: check if already logged in ────────────────────────
  useEffect(() => {
    // Handle OAuth redirect back from provider
    const hash = window.location.hash;
    if (hash.includes('login_ok')) {
      window.location.hash = '';
    }
    if (hash.includes('login_error=')) {
      const msg = decodeURIComponent(hash.split('login_error=')[1] || 'Login failed');
      window.location.hash = '';
      notify(msg, 'error');
      setScreen('login');
      return;
    }

    auth.me()
      .then(userData => {
        setCurrentUser(userData);
        if (userData.theme_key) setThemeKey(userData.theme_key);
        return profilesApi.list();
      })
      .then(profs => {
        setProfiles(profs);
        if (profs.length > 0) {
          setActive(profs[0]);
          setScreen('dashboard');
        } else {
          setScreen('home');
        }
      })
      .catch(() => setScreen('login'));
  }, []);

  // ── Auth ─────────────────────────────────────────────────────
  const handleLogout = async () => {
    try { await auth.logout(); } catch {}
    setCurrentUser(null);
    setProfiles([]);
    setActive(null);
    setScreen('login');
  };

  // ── Theme change (persists to DB) ─────────────────────────────
  const handleThemeChange = async (key) => {
    setThemeKey(key);
    try { await userApi.update({ theme_key: key }); } catch {}
  };

  // ── Profiles ─────────────────────────────────────────────────
  const createProfile = async (name, color) => {
    const p = await profilesApi.create({ name, color });
    setProfiles(prev => [...prev, { ...p, comic_count: 0 }]);
    notify(`Profile "${name}" created`, 'success');
    return p;
  };

  const deleteProfile = async (id) => {
    await profilesApi.remove(id);
    setProfiles(prev => prev.filter(p => p.id !== id));
    if (activeProfile?.id === id) { setActive(null); setScreen('home'); }
    notify('Profile deleted', 'info');
  };

  const updateProfileCount = (profileId, delta) => {
    setProfiles(prev => prev.map(p =>
      p.id === profileId ? { ...p, comic_count: (p.comic_count || 0) + delta } : p
    ));
  };

  if (screen === 'loading') {
    return (
      <div style={{ minHeight:'100vh', background:'#0e0e0e', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',system-ui,sans-serif", overflow:'hidden', position:'relative' }}>
        <style dangerouslySetInnerHTML={{ __html: `
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;900&display=swap');
          @keyframes sv-pulse { 0%,100%{opacity:.25;transform:scale(1)} 50%{opacity:.6;transform:scale(1.015)} }
          @keyframes sv-fadein { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
          @keyframes sv-glow { 0%,100%{border-color:rgba(201,168,76,.35);box-shadow:0 0 0 rgba(201,168,76,0)} 50%{border-color:rgba(201,168,76,.9);box-shadow:0 0 20px rgba(201,168,76,.15)} }
          @keyframes sv-load { 0%{width:0%} 40%{width:55%} 70%{width:76%} 90%{width:92%} 100%{width:100%} }
          @keyframes sv-blink { 0%,100%{opacity:.35} 50%{opacity:.85} }
          .sv-ring{position:absolute;border-radius:50%;border:1px solid rgba(201,168,76,.07);animation:sv-pulse 3s ease-in-out infinite}
          .sv-logo{animation:sv-fadein .8s ease forwards;opacity:0}
          .sv-bar-wrap{animation:sv-fadein .8s .3s ease forwards;opacity:0}
          .sv-icon-box{animation:sv-glow 2.5s ease-in-out infinite}
          .sv-bar-fill{animation:sv-load 2.4s cubic-bezier(.4,0,.2,1) .5s forwards;width:0%}
          .sv-status{animation:sv-blink 1.4s ease-in-out infinite}
        `}} />

        {/* Pulsing rings */}
        <div style={{ position:'absolute', width:340, height:340, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {[120,190,260,330].map((s,i) => (
            <div key={s} className="sv-ring" style={{ width:s, height:s, animationDelay:`${i*0.4}s` }} />
          ))}
        </div>

        {/* Logo */}
        <div className="sv-logo" style={{ position:'relative', zIndex:2, display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
          <div className="sv-icon-box" style={{ width:58, height:58, border:'1.5px solid rgba(201,168,76,.4)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:4 }}>
            <svg width="30" height="30" viewBox="0 0 28 28" fill="none">
              <rect x="4" y="8" width="20" height="16" rx="2" stroke="#c9a84c" strokeWidth="1.5"/>
              <path d="M9 8V6a5 5 0 0 1 10 0v2" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="14" cy="16" r="2.5" stroke="#c9a84c" strokeWidth="1.2"/>
              <line x1="14" y1="18.5" x2="14" y2="21" stroke="#c9a84c" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ display:'flex', alignItems:'baseline', lineHeight:1 }}>
            <span style={{ fontSize:38, fontWeight:900, color:'#c9a84c', letterSpacing:'-1px' }}>Slab</span>
            <span style={{ fontSize:38, fontWeight:300, color:'rgba(201,168,76,.5)', letterSpacing:'2px' }}>Vault</span>
          </div>
          <div style={{ fontSize:11, letterSpacing:'5px', textTransform:'uppercase', color:'rgba(201,168,76,.35)', fontWeight:400, marginTop:2 }}>
            Track &middot; Grade &middot; Collect
          </div>
        </div>

        {/* Progress bar */}
        <div className="sv-bar-wrap" style={{ position:'relative', zIndex:2, marginTop:40, display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
          <div style={{ width:180, height:2, background:'rgba(201,168,76,.1)', borderRadius:2, overflow:'hidden' }}>
            <div className="sv-bar-fill" style={{ height:'100%', background:'#c9a84c', borderRadius:2 }} />
          </div>
          <LoadingStatus />
        </div>
      </div>
    );
  }

  const screenProps = { t, notify, currentUser, profiles, activeProfile, themeKey,
    setThemeKey: handleThemeChange, setScreen, setActive, setProfiles,
    createProfile, deleteProfile, updateProfileCount, onLogout: handleLogout,
    showFinancials, toggleShowFinancials };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;900&display=swap');
    @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');
    *{box-sizing:border-box;margin:0;padding:0}
    button{cursor:pointer;font-family:inherit}
    input,select,textarea{font-family:inherit}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
    @keyframes sv-pulse{0%,100%{opacity:.25;transform:scale(1)}50%{opacity:.6;transform:scale(1.015)}}
    @keyframes sv-fadein{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
    @keyframes sv-glow{0%,100%{border-color:rgba(201,168,76,.35)}50%{border-color:rgba(201,168,76,.9);box-shadow:0 0 20px rgba(201,168,76,.15)}}
    @keyframes sv-blink{0%,100%{opacity:.35}50%{opacity:.85}}
    @keyframes sv-load{0%{width:0%}40%{width:55%}70%{width:76%}90%{width:92%}100%{width:100%}}
    .fade-in{animation:fadeIn 0.22s ease}
    .sv-ring{position:absolute;border-radius:50%;border:1px solid rgba(201,168,76,.07);animation:sv-pulse 3s ease-in-out infinite}
    .sv-logo{animation:sv-fadein .8s ease forwards;opacity:0}
    .sv-bar-wrap{animation:sv-fadein .8s .3s ease forwards;opacity:0}
    .sv-icon-box{animation:sv-glow 2.5s ease-in-out infinite}
    .sv-status{animation:sv-blink 1.4s ease-in-out infinite}
    .sv-bar-fill{animation:sv-load 2.4s cubic-bezier(.4,0,.2,1) .5s forwards;width:0%}
    input::placeholder{color:inherit;opacity:0.35}
    textarea::placeholder{color:inherit;opacity:0.35}
    ::-webkit-scrollbar{width:5px;height:5px}
    ::-webkit-scrollbar-thumb{background:rgba(128,128,128,0.2);border-radius:3px}
    ::-webkit-scrollbar-track{background:transparent}
    .sv-nav-item{display:flex;align-items:center;gap:9px;padding:7px 10px;border-radius:6px;cursor:pointer;font-size:12px;color:rgba(255,255,255,.4);margin-bottom:1px;transition:all 0.12s;user-select:none;border:none;background:none;width:100%;text-align:left}
    .sv-nav-item:hover{background:rgba(255,255,255,.06);color:rgba(255,255,255,.75)}
    .sv-nav-item.active{background:rgba(201,168,76,.15);color:#c9a84c}
    .sv-topbtn{height:30px;padding:0 11px;background:rgba(255,255,255,.07);border:none;border-radius:6px;color:rgba(255,255,255,.6);font-size:12px;cursor:pointer;display:flex;align-items:center;gap:6px;transition:background 0.12s;white-space:nowrap}
    .sv-topbtn:hover{background:rgba(255,255,255,.13);color:rgba(255,255,255,.9)}
    .sv-topbtn.accent{background:rgba(201,168,76,.18);color:#c9a84c}
    .sv-topbtn.accent:hover{background:rgba(201,168,76,.3)}
  `;

  // ── Loading ───────────────────────────────────────────────────
  if (screen === 'loading') {
    return (
      <div style={{ minHeight:'100vh', background:'#0e0e0e', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',system-ui,sans-serif", overflow:'hidden', position:'relative' }}>
        <style dangerouslySetInnerHTML={{ __html: globalStyles }} />
        <div style={{ position:'absolute', width:340, height:340, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {[120,190,260,330].map((s,i) => <div key={s} className="sv-ring" style={{ width:s, height:s, animationDelay:`${i*0.4}s` }} />)}
        </div>
        <div className="sv-logo" style={{ position:'relative', zIndex:2, display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
          <div className="sv-icon-box" style={{ width:58, height:58, border:'1.5px solid rgba(201,168,76,.4)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:4 }}>
            <svg width="30" height="30" viewBox="0 0 28 28" fill="none">
              <rect x="4" y="8" width="20" height="16" rx="2" stroke="#c9a84c" strokeWidth="1.5"/>
              <path d="M9 8V6a5 5 0 0 1 10 0v2" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="14" cy="16" r="2.5" stroke="#c9a84c" strokeWidth="1.2"/>
              <line x1="14" y1="18.5" x2="14" y2="21" stroke="#c9a84c" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ display:'flex', alignItems:'baseline', lineHeight:1 }}>
            <span style={{ fontSize:38, fontWeight:900, color:'#c9a84c', letterSpacing:'-1px' }}>Slab</span>
            <span style={{ fontSize:38, fontWeight:300, color:'rgba(201,168,76,.5)', letterSpacing:'2px' }}>Vault</span>
          </div>
          <div style={{ fontSize:11, letterSpacing:'5px', textTransform:'uppercase', color:'rgba(201,168,76,.35)', fontWeight:400, marginTop:2 }}>
            Track &middot; Grade &middot; Collect
          </div>
        </div>
        <div className="sv-bar-wrap" style={{ position:'relative', zIndex:2, marginTop:40, display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
          <div style={{ width:180, height:2, background:'rgba(201,168,76,.1)', borderRadius:2, overflow:'hidden' }}>
            <div className="sv-bar-fill" style={{ height:'100%', background:'#c9a84c', borderRadius:2 }} />
          </div>
          <LoadingStatus />
        </div>
      </div>
    );
  }

  // ── Login (no shell) ──────────────────────────────────────────
  if (screen === 'login') {
    return (
      <div style={{ minHeight:'100vh', background:t.bg, color:t.text, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif" }}>
        <style dangerouslySetInnerHTML={{ __html: globalStyles }} />
        {notification && <Toast notification={notification} t={t} />}
        <LoginScreen {...screenProps} />
      </div>
    );
  }

  // ── Authenticated shell ───────────────────────────────────────
  return (
    <div style={{ height:'100vh', display:'flex', flexDirection:'column', background:t.bg, color:t.text, fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", overflow:'hidden' }}>
      <style dangerouslySetInnerHTML={{ __html: globalStyles }} />
      {notification && <Toast notification={notification} t={t} />}

      {/* Top bar */}
      <div style={{ height:46, background:'#0a0a0a', borderBottom:'1px solid rgba(255,255,255,.07)', display:'flex', alignItems:'center', padding:'0 14px', gap:10, flexShrink:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'baseline' }}>
          <span style={{ fontSize:17, fontWeight:900, color:'#c9a84c', letterSpacing:'-0.5px' }}>Slab</span>
          <span style={{ fontSize:17, fontWeight:300, color:'rgba(201,168,76,.45)', letterSpacing:'1.5px' }}>Vault</span>
        </div>
        <div style={{ width:1, height:20, background:'rgba(255,255,255,.08)', margin:'0 4px' }} />

        {/* Profile switcher */}
        <ProfileSwitcherDropdown t={t} profiles={profiles} activeProfile={activeProfile}
          onSelect={(p) => { setActive(p); setScreen('dashboard'); }}
          onCreate={createProfile} />

        <div style={{ display:'flex', gap:5, marginLeft:'auto', alignItems:'center' }}>
          <button className="sv-topbtn accent" onClick={() => setScreen('scan')}>
            <i className="ti ti-camera" aria-hidden="true" /> Scan
          </button>
          <button className="sv-topbtn" onClick={() => setScreen('lookup')}>
            <i className="ti ti-search" aria-hidden="true" /> Search
          </button>
          <button className="sv-topbtn" onClick={() => setScreen('importexport')}>
            <i className="ti ti-upload" aria-hidden="true" /> Import
          </button>
          <button className="sv-topbtn" onClick={() => setScreen('importexport')}>
            <i className="ti ti-download" aria-hidden="true" /> Export
          </button>
          <div style={{ width:1, height:20, background:'rgba(255,255,255,.08)', margin:'0 2px' }} />
          <button className="sv-topbtn" onClick={() => setScreen('settings')}>
            <i className="ti ti-settings" aria-hidden="true" />
          </button>
          <div style={{ width:28, height:28, borderRadius:'50%', background:'rgba(201,168,76,.18)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#c9a84c', cursor:'pointer', marginLeft:2 }}
            onClick={() => setScreen('settings')}>
            {currentUser?.display_name?.[0]?.toUpperCase() || 'U'}
          </div>
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* Sidebar */}
        <div style={{ width:192, background:'#0d0d0d', borderRight:'1px solid rgba(255,255,255,.06)', display:'flex', flexDirection:'column', flexShrink:0, overflowY:'auto', padding:'8px 0' }}>
          <SidebarSection label="Collection">
            <SidebarItem icon="ti-layout-grid" label="Dashboard" active={screen==='dashboard'} onClick={() => setScreen('dashboard')} />
            <SidebarItem icon="ti-qrcode"       label="Scan"      active={screen==='scan'}      onClick={() => setScreen('scan')} />
            <SidebarItem icon="ti-search"       label="Lookup"    active={screen==='lookup'}    onClick={() => setScreen('lookup')} />
          </SidebarSection>

          <SidebarSection label="Profiles">
            {profiles.map(p => (
              <SidebarItem key={p.id} icon="ti-books" label={p.name}
                count={p.comic_count || 0}
                active={activeProfile?.id === p.id && ['profile','dashboard'].includes(screen)}
                accent={p.color}
                onClick={() => { setActive(p); setScreen('dashboard'); }} />
            ))}
            <SidebarItem icon="ti-plus" label="New Profile" muted onClick={() => setScreen('home')} />
          </SidebarSection>

          <div style={{ flex:1 }} />

          <SidebarSection label="Tools">
            <SidebarItem icon="ti-file-import" label="Import / Export" active={screen==='importexport'} onClick={() => setScreen('importexport')} />
            <SidebarItem icon="ti-settings"    label="Settings"        active={screen==='settings'}     onClick={() => setScreen('settings')} />
            {currentUser?.role === 'admin' && (
              <SidebarItem icon="ti-shield-check" label="Admin Panel" active={screen==='admin'} onClick={() => setScreen('admin')} accent="#c0392b" />
            )}
          </SidebarSection>

          {/* User row */}
          <div style={{ margin:'4px 8px 4px', borderTop:'1px solid rgba(255,255,255,.05)', paddingTop:8, display:'flex', alignItems:'center', gap:8, padding:'8px 10px' }}>
            <div style={{ width:24, height:24, borderRadius:'50%', background:'rgba(201,168,76,.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#c9a84c', flexShrink:0 }}>
              {currentUser?.display_name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.6)', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {currentUser?.display_name?.split(' ')[0]}
              </div>
              <div style={{ fontSize:9, color:'rgba(255,255,255,.2)', textTransform:'capitalize' }}>{currentUser?.provider}</div>
            </div>
            <button onClick={handleLogout} style={{ background:'none', border:'none', color:'rgba(255,255,255,.2)', cursor:'pointer', fontSize:13, padding:2 }} title="Sign out">
              <i className="ti ti-logout" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', background:t.bg }}>
          {(screen === 'home' || screen === 'profile' || screen === 'scan' || screen === 'lookup' || screen === 'importexport' || screen === 'settings' || screen === 'admin') && (
            <div style={{ flex:1, overflowY:'auto' }}>
              {screen === 'home'         && <HomeScreen         {...screenProps} />}
              {screen === 'profile'      && <ProfileScreen      {...screenProps} />}
              {screen === 'scan'         && <ScanScreen         {...screenProps} />}
              {screen === 'lookup'       && <LookupScreen       {...screenProps} />}
              {screen === 'importexport' && <ImportExportScreen {...screenProps} />}
              {screen === 'settings'     && <SettingsScreen     {...screenProps} />}
              {screen === 'admin'        && <AdminScreen        {...screenProps} />}
            </div>
          )}
          {screen === 'dashboard' && <DashboardScreen {...screenProps} />}
        </div>
      </div>
    </div>
  );
}

// ── Toast notification ────────────────────────────────────────
function Toast({ notification, t }) {
  return (
    <div style={{ position:'fixed', top:18, right:18, background: notification.type==='success'?'#16a34a':notification.type==='error'?'#dc2626':t.accent, color:'#fff', padding:'10px 18px', borderRadius:10, fontSize:14, fontWeight:500, zIndex:9999, animation:'slideDown 0.2s ease', boxShadow:'0 4px 16px rgba(0,0,0,0.3)', maxWidth:320 }}>
      {notification.msg}
    </div>
  );
}

// ── Sidebar helpers ───────────────────────────────────────────
function SidebarSection({ label, children }) {
  return (
    <div style={{ padding:'0 8px', marginBottom:8 }}>
      <div style={{ fontSize:9, fontWeight:600, color:'rgba(255,255,255,.22)', letterSpacing:'1.5px', textTransform:'uppercase', padding:'6px 10px 4px' }}>{label}</div>
      {children}
    </div>
  );
}

function SidebarItem({ icon, label, count, active, onClick, accent, muted }) {
  return (
    <button className={`sv-nav-item${active?' active':''}`} onClick={onClick}
      style={{ color: muted ? 'rgba(255,255,255,.25)' : active ? '#c9a84c' : undefined }}>
      <i className={`ti ${icon}`} style={{ fontSize:14, flexShrink:0, color: active ? '#c9a84c' : accent || undefined }} aria-hidden="true" />
      <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
      {count !== undefined && (
        <span style={{ fontSize:10, background:'rgba(255,255,255,.07)', padding:'1px 6px', borderRadius:10, color:'rgba(255,255,255,.3)', flexShrink:0 }}>{count}</span>
      )}
    </button>
  );
}

// ── Profile switcher dropdown in top bar ─────────────────────
function ProfileSwitcherDropdown({ t, profiles, activeProfile, onSelect, onCreate }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    const colors = ['#c9a84c','#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4'];
    const color = colors[profiles.length % colors.length];
    const p = await onCreate(newName.trim(), color);
    onSelect(p);
    setNewName(''); setOpen(false);
    setCreating(false);
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button onClick={() => setOpen(!open)}
        style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 10px', background:'rgba(255,255,255,.06)', border:'none', borderRadius:6, cursor:'pointer', color:'rgba(255,255,255,.7)', fontSize:12 }}>
        <i className="ti ti-books" style={{ fontSize:13, color:'rgba(201,168,76,.6)' }} aria-hidden="true" />
        <span style={{ maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {activeProfile?.name || 'Select Profile'}
        </span>
        {activeProfile && <span style={{ fontSize:10, color:'rgba(255,255,255,.3)' }}>{activeProfile.comic_count || 0}</span>}
        <i className="ti ti-chevron-down" style={{ fontSize:11, color:'rgba(255,255,255,.3)' }} aria-hidden="true" />
      </button>

      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, background:'#1a1a1a', border:'1px solid rgba(255,255,255,.1)', borderRadius:10, minWidth:220, zIndex:999, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.5)', animation:'slideDown 0.15s ease' }}>
          {profiles.map(p => (
            <button key={p.id} onClick={() => { onSelect(p); setOpen(false); }}
              style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 14px', background: activeProfile?.id===p.id ? 'rgba(201,168,76,.1)' : 'none', border:'none', cursor:'pointer', color: activeProfile?.id===p.id ? '#c9a84c' : 'rgba(255,255,255,.65)', fontSize:13, textAlign:'left', transition:'background 0.1s' }}
              onMouseEnter={e => { if (activeProfile?.id !== p.id) e.currentTarget.style.background='rgba(255,255,255,.05)'; }}
              onMouseLeave={e => { if (activeProfile?.id !== p.id) e.currentTarget.style.background='none'; }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:p.color, flexShrink:0 }} />
              <span style={{ flex:1 }}>{p.name}</span>
              <span style={{ fontSize:11, color:'rgba(255,255,255,.25)' }}>{p.comic_count || 0}</span>
              {activeProfile?.id === p.id && <i className="ti ti-check" style={{ fontSize:12 }} aria-hidden="true" />}
            </button>
          ))}
          <div style={{ borderTop:'1px solid rgba(255,255,255,.07)', padding:'8px 10px', display:'flex', gap:6 }}>
            <input value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key==='Enter' && handleCreate()}
              placeholder="New profile name…"
              style={{ flex:1, background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', borderRadius:6, padding:'6px 10px', color:'rgba(255,255,255,.7)', fontSize:12, outline:'none' }} />
            <button onClick={handleCreate} disabled={creating || !newName.trim()}
              style={{ background:'rgba(201,168,76,.2)', border:'none', borderRadius:6, padding:'6px 12px', color:'#c9a84c', fontSize:12, fontWeight:600, opacity: creating || !newName.trim() ? 0.5 : 1 }}>
              {creating ? '…' : '+'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SlabVault Logo component ──────────────────────────────────
function SlabVaultLogo({ size = 'lg', accent, muted }) {
  const sizes = {
    lg: { slab: 48, vault: 48, tag: 13, gap: 0 },
    md: { slab: 32, vault: 32, tag: 11, gap: 0 },
    sm: { slab: 20, vault: 20, tag: 10, gap: 0 },
  };
  const s = sizes[size];
  return (
    <div style={{ textAlign:'center' }}>
      <div style={{ display:'inline-flex', alignItems:'baseline', gap:0 }}>
        <span style={{ fontSize:s.slab, fontWeight:900, letterSpacing:'-1px', color: accent, lineHeight:1 }}>Slab</span>
        <span style={{ fontSize:s.vault, fontWeight:300, letterSpacing:'2px', color: muted || accent, lineHeight:1 }}>Vault</span>
      </div>
      {size === 'lg' && (
        <div style={{ fontSize:s.tag, color: muted, letterSpacing:'4px', textTransform:'uppercase', marginTop:6, fontWeight:400 }}>
          Track · Grade · Collect
        </div>
      )}
    </div>
  );
}

// ── Loading status cycler ─────────────────────────────────────
function LoadingStatus() {
  const msgs = ['Opening the vault', 'Loading collection', 'Authenticating', 'Almost ready'];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % msgs.length), 700);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="sv-status" style={{ fontSize:11, letterSpacing:'2px', textTransform:'uppercase', color:'rgba(201,168,76,.35)', fontWeight:400 }}>
      {msgs[idx]}
    </div>
  );
}
function LoginScreen({ t, themeKey, setThemeKey }) {
  const [showThemes, setShowThemes] = useState(false);

  return (
    <div className="fade-in" style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'2rem 1rem', gap:32 }}>

      <SlabVaultLogo size="lg" accent={THEMES[themeKey].accent} muted={THEMES[themeKey].textMuted} />

      <div style={{ background:THEMES[themeKey].surface, border:`1px solid ${THEMES[themeKey].border}`, borderRadius:12, padding:'2rem 2.5rem', width:'100%', maxWidth:360, display:'flex', flexDirection:'column', gap:12 }}>
        <p style={{ fontSize:12, color:THEMES[themeKey].textMuted, textAlign:'center', marginBottom:4, textTransform:'uppercase', letterSpacing:2, fontWeight:500 }}>
          Sign in to your vault
        </p>

        <a href="#" onClick={e => { e.preventDefault(); sessionStorage.setItem('oauth_provider','google'); window.location.href = auth.googleLoginUrl(); }} style={{ textDecoration:'none' }}>
          <OAuthButton t={THEMES[themeKey]} icon="G" label="Continue with Google" color="#4285f4" />
        </a>
        <a href="#" onClick={e => { e.preventDefault(); sessionStorage.setItem('oauth_provider','facebook'); window.location.href = auth.facebookLoginUrl(); }} style={{ textDecoration:'none' }}>
          <OAuthButton t={THEMES[themeKey]} icon="f" label="Continue with Facebook" color="#1877f2" />
        </a>

        <div style={{ height:1, background:THEMES[themeKey].border, margin:'4px 0' }} />
        <p style={{ fontSize:11, color:THEMES[themeKey].textMuted, textAlign:'center', lineHeight:1.7 }}>
          Your collection is stored privately. We never sell your data.
        </p>
      </div>

      <button onClick={() => setShowThemes(!showThemes)}
        style={{ background:'none', border:'none', color:THEMES[themeKey].textMuted, fontSize:12, cursor:'pointer', letterSpacing:1, textTransform:'uppercase' }}>
        {THEMES[themeKey].icon} {showThemes ? 'Hide themes' : 'Change theme'}
      </button>

      {showThemes && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'center', maxWidth:440 }}>
          {Object.entries(THEMES).map(([k, th]) => (
            <button key={k} onClick={() => { setThemeKey(k); setShowThemes(false); }}
              style={{ background:th.surface, border:`2px solid ${themeKey===k?th.accent:th.border}`, borderRadius:8, padding:'8px 16px', color:th.text, fontSize:12, cursor:'pointer', fontWeight: themeKey===k?600:400 }}>
              {th.icon} {th.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OAuthButton({ t, icon, label, color }) {
  return (
    <div style={{ background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:10, padding:'11px 20px', fontSize:14, fontWeight:500, display:'flex', alignItems:'center', gap:12, justifyContent:'center', color:t.text, width:'100%', cursor:'pointer' }}>
      <span style={{ width:22, height:22, borderRadius:4, background:color, color:'#fff', fontSize:12, fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{icon}</span>
      {label}
    </div>
  );
}

// ── Home ──────────────────────────────────────────────────────
function HomeScreen({ t, currentUser, profiles, setScreen, setActive, createProfile, deleteProfile, notify }) {
  const [newName, setNewName]   = useState('');
  const [showCreate, setCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const totalComics = profiles.reduce((s, p) => s + (p.comic_count || 0), 0);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createProfile(newName.trim(), randomColor());
      setNewName('');
      setCreate(false);
    } catch (e) { notify(e.message, 'error'); }
    setCreating(false);
  };

  return (
    <div className="fade-in" style={{ maxWidth:700, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ display:'inline-flex', alignItems:'baseline', gap:0, lineHeight:1 }}>
            <span style={{ fontSize:22, fontWeight:900, letterSpacing:'-0.5px', color:t.accent }}>Slab</span>
            <span style={{ fontSize:22, fontWeight:300, letterSpacing:'1px', color:t.textMuted }}>Vault</span>
          </div>
          <div style={{ fontSize:12, color:t.textMuted, marginTop:2 }}>Welcome back, {currentUser?.display_name?.split(' ')[0]}</div>
        </div>
        <button onClick={() => setScreen('settings')} style={btnStyle(t)}>⚙ Settings</button>
      </header>

      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        {[{ label:'Profiles', val:profiles.length }, { label:'Total Comics', val:totalComics }].map(s => (
          <div key={s.label} style={{ flex:'1 1 100px', background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:'14px 18px' }}>
            <div style={{ fontSize:24, fontWeight:700 }}>{s.val}</div>
            <div style={{ fontSize:12, color:t.textMuted, marginTop:2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap' }}>
        <ActionBtn t={t} emoji="📷" label="Scan Barcode" onClick={() => { setActive(null); setScreen('scan'); }} accent />
        <ActionBtn t={t} emoji="🔍" label="Search / Lookup" onClick={() => { setActive(null); setScreen('lookup'); }} />
      </div>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <h2 style={{ fontSize:16, fontWeight:600 }}>Profiles</h2>
        <button onClick={() => setCreate(!showCreate)} style={{ background:t.accentSoft, border:`1px solid ${t.border}`, color:t.accent, borderRadius:8, padding:'6px 12px', fontSize:13, fontWeight:500 }}>+ New Profile</button>
      </div>

      {showCreate && (
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:16, marginBottom:12, display:'flex', gap:10, animation:'slideDown 0.2s ease' }}>
          <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key==='Enter' && handleCreate()}
            placeholder="Profile name…"
            style={{ flex:1, background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'9px 13px', color:t.text, fontSize:14, outline:'none' }} />
          <button onClick={handleCreate} disabled={creating || !newName.trim()}
            style={{ background:t.accent, border:'none', borderRadius:8, padding:'9px 16px', color:'#fff', fontWeight:600, fontSize:14, opacity:creating?0.7:1 }}>
            {creating ? '…' : 'Create'}
          </button>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {profiles.map(p => (
          <ProfileCard key={p.id} t={t} profile={p} onOpen={() => { setActive(p); setScreen('dashboard'); }} onDelete={() => deleteProfile(p.id)} />
        ))}
        {profiles.length === 0 && <EmptyState t={t} icon="📚" title="No profiles yet" sub="Create one to start tracking your comics." />}
      </div>
    </div>
  );
}

function ProfileCard({ t, profile, onOpen, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background:hover?t.surfaceAlt:t.surface, border:`1px solid ${hover?profile.color:t.border}`, borderRadius:12, padding:'14px 18px', cursor:'pointer', display:'flex', alignItems:'center', gap:14, transition:'all 0.15s' }}>
      <div onClick={onOpen} style={{ display:'flex', alignItems:'center', gap:14, flex:1 }}>
        <div style={{ width:44, height:44, borderRadius:10, background:profile.color+'22', border:`2px solid ${profile.color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>📚</div>
        <div>
          <div style={{ fontWeight:600, fontSize:15 }}>{profile.name}</div>
          <div style={{ fontSize:12, color:t.textMuted, marginTop:2 }}>{profile.comic_count || 0} issue{profile.comic_count !== 1 ? 's' : ''}</div>
        </div>
      </div>
      <button onClick={e => { e.stopPropagation(); if(window.confirm(`Delete "${profile.name}" and all its comics?`)) onDelete(); }}
        style={{ background:'none', border:'none', color:t.textMuted, fontSize:16, padding:'4px 8px', opacity:hover?1:0, transition:'opacity 0.15s' }}>🗑</button>
      <div onClick={onOpen} style={{ color:t.textMuted, fontSize:18 }}>›</div>
    </div>
  );
}

// ── Profile ───────────────────────────────────────────────────
function ProfileScreen({ t, activeProfile, notify, setScreen, updateProfileCount, showFinancials }) {
  const [comics, setComics]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchComics = useCallback(async (pg = 1, q = '') => {
    if (!activeProfile) return;
    setLoading(true);
    try {
      const res = await comicsApi.list(activeProfile.id, { page: pg, limit: 50, ...(q ? { search: q } : {}) });
      setComics(res.comics);
      setTotalPages(res.pages);
      setPage(pg);
    } catch (e) { notify(e.message, 'error'); }
    setLoading(false);
  }, [activeProfile]);

  useEffect(() => { fetchComics(); }, [fetchComics]);

  const handleSearch = (q) => { setSearch(q); fetchComics(1, q); };

  const removeComic = async (id) => {
    try {
      await comicsApi.remove(id);
      setComics(prev => prev.filter(c => c.id !== id));
      updateProfileCount(activeProfile.id, -1);
      notify('Comic removed from collection', 'info');
    } catch (e) { notify(e.message, 'error'); }
  };

  const updateComic = (updated) => {
    setComics(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
    notify('Saved ✓', 'success');
  };

  if (!activeProfile) { setScreen('home'); return null; }

  return (
    <div className="fade-in" style={{ maxWidth:700, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <header style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <button onClick={() => setScreen('home')} style={btnStyle(t)}>← Back</button>
        <div style={{ flex:1 }}>
          <h1 style={{ fontWeight:700, fontSize:20 }}>{activeProfile.name}</h1>
          <div style={{ fontSize:12, color:t.textMuted }}>{activeProfile.comic_count || 0} comics</div>
        </div>
        <ActionBtn t={t} emoji="📊" label="Dashboard" onClick={() => setScreen('dashboard')} />
        <ActionBtn t={t} emoji="📷" label="Scan" onClick={() => setScreen('scan')} accent />
        <ActionBtn t={t} emoji="🔍" label="Search" onClick={() => setScreen('lookup')} />
        <ActionBtn t={t} emoji="📋" label="Import/Export" onClick={() => setScreen('importexport')} />
      </header>

      {showFinancials && !loading && comics.length > 0 && (() => {
        const withCost = comics.filter(c => c.purchase_price);
        const withSale = comics.filter(c => c.sold_price);
        const totalCost = withCost.reduce((s, c) => s + parseFloat(c.purchase_price), 0);
        const totalSold = withSale.reduce((s, c) => s + parseFloat(c.sold_price), 0);
        const pl = totalSold - withSale.reduce((s, c) => s + (c.purchase_price ? parseFloat(c.purchase_price) : 0), 0);
        if (!withCost.length) return null;
        return (
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
            <div style={{ flex:'1 1 100px', background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, padding:'10px 14px' }}>
              <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Total Invested</div>
              <div style={{ fontSize:18, fontWeight:700, color:t.text, marginTop:2 }}>${totalCost.toFixed(2)}</div>
              <div style={{ fontSize:11, color:t.textMuted }}>{withCost.length} comics tracked</div>
            </div>
            {withSale.length > 0 && (
              <div style={{ flex:'1 1 100px', background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, padding:'10px 14px' }}>
                <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Total Sold</div>
                <div style={{ fontSize:18, fontWeight:700, color:'#16a34a', marginTop:2 }}>${totalSold.toFixed(2)}</div>
                <div style={{ fontSize:11, color:t.textMuted }}>{withSale.length} comics sold</div>
              </div>
            )}
            {withSale.length > 0 && (
              <div style={{ flex:'1 1 100px', background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, padding:'10px 14px' }}>
                <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Realized P&L</div>
                <div style={{ fontSize:18, fontWeight:700, color: pl >= 0 ? '#16a34a' : '#dc2626', marginTop:2 }}>{pl >= 0 ? '+' : ''}${pl.toFixed(2)}</div>
              </div>
            )}
          </div>
        );
      })()}

      <input value={search} onChange={e => handleSearch(e.target.value)} placeholder="Search collection…"
        style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, padding:'10px 16px', color:t.text, fontSize:14, marginBottom:14, outline:'none' }} />

      {loading && <div style={{ textAlign:'center', padding:'3rem', color:t.textMuted }}><Spinner color={t.accent} /></div>}

      {!loading && comics.length === 0 && (
        <EmptyState t={t} icon="📭" title="No comics yet" sub="Scan a barcode or search to add your first issue." />
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {comics.map(c => <ComicRow key={c.id} t={t} comic={c} onRemove={removeComic} onUpdate={updateComic} />)}
      </div>

      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:20 }}>
          <button disabled={page <= 1} onClick={() => fetchComics(page - 1, search)} style={btnStyle(t)}>← Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:t.textMuted }}>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => fetchComics(page + 1, search)} style={btnStyle(t)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function ComicRow({ t, comic, onRemove, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState({
    condition_grade: comic.condition_grade || '',
    personal_notes:  comic.personal_notes  || '',
    is_read:         !!comic.is_read,
    purchase_price:  comic.purchase_price  || '',
    purchase_date:   comic.purchase_date   || '',
    purchase_venue:  comic.purchase_venue  || '',
    sold_price:      comic.sold_price      || '',
    sold_date:       comic.sold_date       || '',
    sold_venue:      comic.sold_venue      || '',
  });

  // GRADES defined globally

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await comicsApi.update(comic.id, draft);
      onUpdate(updated);
      setEditing(false);
    } catch (e) { }
    setSaving(false);
  };

  const gradeColor = (g) => {
    if (!g) return t.textMuted;
    const n = parseFloat(g);
    if (n >= 9.0) return '#16a34a';
    if (n >= 7.0) return '#2563eb';
    if (n >= 4.0) return '#d97706';
    return '#dc2626';
  };

  return (
    <div style={{ background:t.surface, border:`1px solid ${expanded ? t.accent : t.border}`, borderRadius:12, overflow:'hidden', transition:'border-color 0.15s' }}>

      {/* ── Main row ── */}
      <div style={{ padding:'12px 14px', display:'flex', gap:12, alignItems:'flex-start', cursor:'pointer' }}
        onClick={() => { setExpanded(!expanded); setConfirmDelete(false); }}>

        {comic.cover_url
          ? <img src={comic.cover_url} alt={comic.title} style={{ width:52, height:74, objectFit:'cover', borderRadius:6, flexShrink:0 }} onError={e => e.target.style.display='none'} />
          : <div style={{ width:52, height:74, background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>📖</div>
        }

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:8, flexWrap:'wrap' }}>
            <div style={{ fontWeight:700, fontSize:14, color:t.text, flex:1, minWidth:0 }}>{comic.title}</div>
            {comic.is_read == 1 && <span style={{ background:'#16a34a22', color:'#16a34a', fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:5, flexShrink:0 }}>READ</span>}
            {comic.source_api && <span style={{ background:t.accentSoft, color:t.accent, fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:5, flexShrink:0 }}>{comic.source_api}</span>}
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginTop:3 }}>
            {comic.series && comic.series !== comic.title && <span style={{ fontSize:12, color:t.textMuted }}>{comic.series}</span>}
            {comic.issue_number && <span style={{ fontSize:13, color:t.accent, fontWeight:600 }}>#{comic.issue_number}</span>}
            {comic.publisher    && <span style={{ fontSize:12, color:t.textMuted }}>{comic.publisher}</span>}
            {comic.publish_year && <span style={{ fontSize:12, color:t.textMuted }}>{comic.publish_year}</span>}
          </div>
          {comic.condition_grade && (
            <div style={{ marginTop:4 }}>
              <span style={{ fontSize:11, fontWeight:700, color: gradeColor(comic.condition_grade), background: gradeColor(comic.condition_grade)+'18', padding:'2px 8px', borderRadius:5 }}>
                {comic.condition_grade}
              </span>
            </div>
          )}
          {comic.personal_notes && !expanded && (
            <div style={{ fontSize:11, color:t.textMuted, marginTop:4, fontStyle:'italic', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:300 }}>
              "{comic.personal_notes}"
            </div>
          )}
        </div>

        <div style={{ color:t.textMuted, fontSize:14, flexShrink:0, transition:'transform 0.2s', transform: expanded?'rotate(180deg)':'rotate(0deg)' }}>▾</div>
      </div>

      {/* ── Expanded detail panel ── */}
      {expanded && (
        <div style={{ borderTop:`1px solid ${t.border}`, padding:'14px 16px', display:'flex', flexDirection:'column', gap:14 }}>

          {/* All metadata fields */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:10 }}>
            {[
              { label:'Series',        val: comic.series },
              { label:'Issue',         val: comic.issue_number ? `#${comic.issue_number}` : null },
              { label:'Publisher',     val: comic.publisher },
              { label:'Year',          val: comic.publish_year },
              { label:'Publish Date',  val: comic.publish_date },
              { label:'Writer(s)',     val: comic.writers },
              { label:'Artist(s)',     val: comic.artists },
              { label:'Colorist(s)',   val: comic.colorists },
              { label:'Letterer(s)',   val: comic.letterers },
              { label:'Cover Artist',  val: comic.cover_artists },
              { label:'Editor(s)',     val: comic.editors },
              { label:'ISBN',          val: comic.isbn },
              { label:'UPC',           val: comic.upc },
              { label:'Barcode',       val: comic.barcode },
              { label:'Comic Vine ID', val: comic.comicvine_id },
              { label:'Source',        val: comic.source_api },
              { label:'Added',         val: new Date(comic.added_at).toLocaleDateString() },
              { label:'Updated',       val: comic.updated_at ? new Date(comic.updated_at).toLocaleDateString() : null },
            ].filter(f => f.val).map(f => (
              <div key={f.label} style={{ background:t.surfaceAlt, borderRadius:8, padding:'8px 10px' }}>
                <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>{f.label}</div>
                <div style={{ fontSize:13, color:t.text, marginTop:2, wordBreak:'break-all' }}>{f.val}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          {comic.description && (
            <div style={{ background:t.surfaceAlt, borderRadius:8, padding:'10px 12px' }}>
              <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:5 }}>Description</div>
              <div style={{ fontSize:13, color:t.textMuted, lineHeight:1.6 }}>{comic.description}</div>
            </div>
          )}

          {/* Editable section */}
          {!editing ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <div style={{ flex:'1 1 140px', background:t.surfaceAlt, borderRadius:8, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Condition</div>
                  <div style={{ fontSize:13, color: comic.condition_grade ? gradeColor(comic.condition_grade) : t.textMuted, marginTop:2, fontWeight: comic.condition_grade ? 700 : 400 }}>
                    {comic.condition_grade || 'Not set'}
                  </div>
                </div>
                <div style={{ flex:'1 1 60px', background:t.surfaceAlt, borderRadius:8, padding:'8px 10px' }}>
                  <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Read</div>
                  <div style={{ fontSize:13, color: comic.is_read ? '#16a34a' : t.textMuted, marginTop:2 }}>{comic.is_read ? '✓ Yes' : 'No'}</div>
                </div>
              </div>
              {comic.personal_notes && (
                <div style={{ background:t.surfaceAlt, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:4 }}>My Notes</div>
                  <div style={{ fontSize:13, color:t.text, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{comic.personal_notes}</div>
                </div>
              )}
              <button onClick={e => { e.stopPropagation(); setEditing(true); setDraft({ condition_grade: comic.condition_grade||'', personal_notes: comic.personal_notes||'', is_read: !!comic.is_read, purchase_price: comic.purchase_price||'', purchase_date: comic.purchase_date||'', purchase_venue: comic.purchase_venue||'', sold_price: comic.sold_price||'', sold_date: comic.sold_date||'', sold_venue: comic.sold_venue||'' }); }}
                style={{ background:t.accentSoft, border:`1px solid ${t.border}`, borderRadius:8, padding:'8px 14px', color:t.accent, fontSize:13, fontWeight:500, alignSelf:'flex-start' }}>
                ✏️ Edit Notes & Condition
              </button>

              {/* Purchase / Sale summary */}
              {(comic.purchase_price || comic.sold_price) && (
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  {comic.purchase_price && (
                    <div style={{ flex:'1 1 140px', background:t.surfaceAlt, borderRadius:8, padding:'8px 10px' }}>
                      <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Purchased</div>
                      <div style={{ fontSize:15, fontWeight:700, color:t.text, marginTop:2 }}>${parseFloat(comic.purchase_price).toFixed(2)}</div>
                      {comic.purchase_date && <div style={{ fontSize:11, color:t.textMuted }}>{comic.purchase_date}</div>}
                      {comic.purchase_venue && <div style={{ fontSize:11, color:t.textMuted }}>{comic.purchase_venue}</div>}
                    </div>
                  )}
                  {comic.sold_price && (
                    <div style={{ flex:'1 1 140px', background:t.surfaceAlt, borderRadius:8, padding:'8px 10px' }}>
                      <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>Sold</div>
                      <div style={{ fontSize:15, fontWeight:700, color:'#16a34a', marginTop:2 }}>${parseFloat(comic.sold_price).toFixed(2)}</div>
                      {comic.sold_date && <div style={{ fontSize:11, color:t.textMuted }}>{comic.sold_date}</div>}
                      {comic.sold_venue && <div style={{ fontSize:11, color:t.textMuted }}>{comic.sold_venue}</div>}
                    </div>
                  )}
                  {comic.purchase_price && comic.sold_price && (
                    <div style={{ flex:'1 1 100px', background:t.surfaceAlt, borderRadius:8, padding:'8px 10px' }}>
                      <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>P&L</div>
                      {(() => {
                        const pl = parseFloat(comic.sold_price) - parseFloat(comic.purchase_price);
                        return <div style={{ fontSize:15, fontWeight:700, color: pl >= 0 ? '#16a34a' : '#dc2626', marginTop:2 }}>{pl >= 0 ? '+' : ''}${pl.toFixed(2)}</div>;
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div onClick={e => e.stopPropagation()} style={{ display:'flex', flexDirection:'column', gap:12, background:t.surfaceAlt, borderRadius:10, padding:14 }}>
              <div>
                <label style={{ fontSize:12, color:t.textMuted, fontWeight:600, display:'block', marginBottom:5 }}>CONDITION GRADE</label>
                <select value={draft.condition_grade} onChange={e => setDraft(d => ({ ...d, condition_grade: e.target.value }))}
                  style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:8, padding:'8px 12px', color: draft.condition_grade ? gradeColor(draft.condition_grade) : t.textMuted, fontSize:13, fontWeight:700, outline:'none' }}>
                  {GRADES.map(g => <option key={g} value={g}>{g || '— Not set —'}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, color:t.textMuted, fontWeight:600, display:'block', marginBottom:5 }}>PERSONAL NOTES</label>
                <textarea value={draft.personal_notes} onChange={e => setDraft(d => ({ ...d, personal_notes: e.target.value }))}
                  placeholder="Variant cover, purchase price, condition details, where you got it…"
                  rows={4}
                  style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:8, padding:'8px 12px', color:t.text, fontSize:13, outline:'none', resize:'vertical', lineHeight:1.5 }} />
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:t.text, userSelect:'none' }}>
                  <input type="checkbox" checked={draft.is_read} onChange={e => setDraft(d => ({ ...d, is_read: e.target.checked }))}
                    style={{ width:16, height:16, accentColor:t.accent }} />
                  Mark as read
                </label>
              </div>

              {/* Purchase details */}
              <div style={{ borderTop:`1px solid ${t.border}`, paddingTop:12 }}>
                <div style={{ fontSize:12, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Purchase Details</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, display:'block', marginBottom:4 }}>PRICE PAID ($)</label>
                    <input type="number" min="0" step="0.01" value={draft.purchase_price} onChange={e => setDraft(d => ({ ...d, purchase_price: e.target.value }))}
                      placeholder="0.00"
                      style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 10px', color:t.text, fontSize:13, outline:'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, display:'block', marginBottom:4 }}>DATE PURCHASED</label>
                    <input type="date" value={draft.purchase_date} onChange={e => setDraft(d => ({ ...d, purchase_date: e.target.value }))}
                      style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 10px', color:t.text, fontSize:13, outline:'none' }} />
                  </div>
                </div>
                <div style={{ marginTop:8 }}>
                  <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, display:'block', marginBottom:4 }}>WHERE PURCHASED</label>
                  <input type="text" value={draft.purchase_venue} onChange={e => setDraft(d => ({ ...d, purchase_venue: e.target.value }))}
                    placeholder="e.g. eBay, Local shop, Comic Con…"
                    style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 10px', color:t.text, fontSize:13, outline:'none' }} />
                </div>
              </div>

              {/* Sale details */}
              <div style={{ borderTop:`1px solid ${t.border}`, paddingTop:12 }}>
                <div style={{ fontSize:12, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>Sale Details <span style={{ fontSize:10, fontWeight:400, color:t.textMuted }}>(leave blank if not sold)</span></div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, display:'block', marginBottom:4 }}>SOLD FOR ($)</label>
                    <input type="number" min="0" step="0.01" value={draft.sold_price} onChange={e => setDraft(d => ({ ...d, sold_price: e.target.value }))}
                      placeholder="0.00"
                      style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 10px', color:t.text, fontSize:13, outline:'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, display:'block', marginBottom:4 }}>DATE SOLD</label>
                    <input type="date" value={draft.sold_date} onChange={e => setDraft(d => ({ ...d, sold_date: e.target.value }))}
                      style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 10px', color:t.text, fontSize:13, outline:'none' }} />
                  </div>
                </div>
                <div style={{ marginTop:8 }}>
                  <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, display:'block', marginBottom:4 }}>WHERE SOLD</label>
                  <input type="text" value={draft.sold_venue} onChange={e => setDraft(d => ({ ...d, sold_venue: e.target.value }))}
                    placeholder="e.g. eBay, Heritage, MyComicShop…"
                    style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 10px', color:t.text, fontSize:13, outline:'none' }} />
                </div>
                {/* Live P&L preview */}
                {draft.purchase_price && draft.sold_price && (
                  <div style={{ marginTop:10, background:t.surface, borderRadius:8, padding:'10px 12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:12, color:t.textMuted }}>P&L Preview</span>
                    {(() => {
                      const pl = parseFloat(draft.sold_price || 0) - parseFloat(draft.purchase_price || 0);
                      return <span style={{ fontSize:15, fontWeight:700, color: pl >= 0 ? '#16a34a' : '#dc2626' }}>{pl >= 0 ? '+' : ''}${pl.toFixed(2)}</span>;
                    })()}
                  </div>
                )}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex:1, background:t.accent, border:'none', borderRadius:8, padding:'9px', color:'#fff', fontWeight:600, fontSize:13, opacity:saving?0.7:1 }}>
                  {saving ? 'Saving…' : '✓ Save'}
                </button>
                <button onClick={() => setEditing(false)}
                  style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:8, padding:'9px 16px', color:t.textMuted, fontSize:13 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Delete confirmation */}
          {!confirmDelete ? (
            <button onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
              style={{ background:'none', border:`1px solid #dc262644`, borderRadius:8, padding:'7px 14px', color:'#dc2626', fontSize:12, fontWeight:500, alignSelf:'flex-start' }}>
              🗑 Remove from collection
            </button>
          ) : (
            <div onClick={e => e.stopPropagation()} style={{ background:'#7f1d1d18', border:'1px solid #dc262655', borderRadius:8, padding:'12px 14px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <div style={{ flex:1, fontSize:13, color:'#dc2626', fontWeight:500 }}>Remove "{comic.title}" from your collection?</div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => { onRemove(comic.id); }}
                  style={{ background:'#dc2626', border:'none', borderRadius:7, padding:'7px 16px', color:'#fff', fontWeight:600, fontSize:13 }}>
                  Remove
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:7, padding:'7px 14px', color:t.textMuted, fontSize:13 }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ── Scan ──────────────────────────────────────────────────────
function ScanScreen({ t, activeProfile, profiles, setScreen, updateProfileCount, notify }) {
  const [mode, setMode]               = useState('idle');
  const [barcode, setBarcode]         = useState('');
  const [manualInput, setManualInput] = useState('');
  const [results, setResults]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [targetId, setTargetId]       = useState(activeProfile?.id || profiles[0]?.id || '');
  const [adding, setAdding]           = useState(false);
  const [scanStatus, setScanStatus]   = useState('Point camera at barcode');
  const videoRef    = useRef(null);
  const readerRef   = useRef(null);

  useEffect(() => () => stopCamera(), []);

  const stopCamera = () => {
    try { readerRef.current?.reset(); } catch {}
    readerRef.current = null;
  };

  const startCamera = async () => {
    setMode('camera');
    setScanStatus('Starting camera…');
    try {
      // Dynamically import ZXing so it doesn't block initial load
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;

      // Prefer back camera on mobile
      const devices = await BrowserMultiFormatReader.listVideoInputDevices();
      const backCam = devices.find(d =>
        d.label.toLowerCase().includes('back') ||
        d.label.toLowerCase().includes('rear') ||
        d.label.toLowerCase().includes('environment')
      );
      const deviceId = backCam?.deviceId || devices[devices.length - 1]?.deviceId;

      setScanStatus('Point camera at barcode');

      await reader.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
        if (result) {
          const text = result.getText();
          stopCamera();
          doLookup(text);
        }
        // Suppress all scan errors — NotFoundException fires every frame normally
      });
    } catch (e) {
      stopCamera();
      if (e.name === 'NotAllowedError') {
        notify('Camera permission denied. Please allow camera access in your browser settings.', 'error');
      } else if (e.name === 'NotFoundError') {
        notify('No camera found on this device.', 'error');
      } else {
        notify('Camera error: ' + e.message, 'error');
      }
      setMode('manual');
    }
  };

  const doLookup = async (code) => {
    stopCamera();
    setBarcode(code);
    setMode('result');
    setLoading(true);
    setResults(null);
    try {
      const data = await lookup.barcode(code);
      setResults(Array.isArray(data) ? data : [data]);
    } catch (e) {
      setResults([{ error: e.message, barcode: code }]);
    }
    setLoading(false);
  };

  const handleAdd = async (comic) => {
    setAdding(true);
    try {
      await comicsApi.add({ ...comic, profile_id: parseInt(targetId), barcode: comic.barcode || barcode || manualInput });
      updateProfileCount(parseInt(targetId), 1);
      notify(`"${comic.title}" added ✓`, 'success');
      setResults(null); setBarcode(''); setManualInput(''); setMode('idle');
    } catch (e) { notify(e.message, 'error'); }
    setAdding(false);
  };

  return (
    <div className="fade-in" style={{ maxWidth:600, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <header style={{ display:'flex', alignItems:'center', gap:12, marginBottom:22 }}>
        <button onClick={() => { stopCamera(); setScreen(activeProfile ? 'profile' : 'home'); }} style={btnStyle(t)}>← Back</button>
        <h1 style={{ fontWeight:700, fontSize:20 }}>Scan Barcode</h1>
      </header>

      {mode === 'idle' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <BigBtn t={t} emoji="📷" title="Use Camera" sub="Scan UPC · EAN · ISBN · QR" onClick={startCamera} accent />
          <BigBtn t={t} emoji="✏️" title="Enter Manually" sub="Type a barcode or ISBN number" onClick={() => setMode('manual')} />
        </div>
      )}

      {mode === 'camera' && (
        <div style={{ textAlign:'center' }}>
          <div style={{ position:'relative', background:'#000', borderRadius:14, overflow:'hidden', marginBottom:14, aspectRatio:'4/3' }}>
            <video ref={videoRef} style={{ width:'100%', height:'100%', objectFit:'cover' }} playsInline muted />
            {/* Scan guide overlay */}
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
              <div style={{ width:'75%', height:'25%', position:'relative' }}>
                {/* Corner markers */}
                {[['0','0'],['0','auto'],['auto','0'],['auto','auto']].map(([t,b], i) => (
                  <div key={i} style={{ position:'absolute', width:20, height:20, top: i<2?0:'auto', bottom: i>=2?0:'auto', left: i%2===0?0:'auto', right: i%2===1?0:'auto',
                    borderTop: i<2?`3px solid ${THEMES[Object.keys(THEMES)[0]].accent}`:'none',
                    borderBottom: i>=2?`3px solid ${THEMES[Object.keys(THEMES)[0]].accent}`:'none',
                    borderLeft: i%2===0?`3px solid ${THEMES[Object.keys(THEMES)[0]].accent}`:'none',
                    borderRight: i%2===1?`3px solid ${THEMES[Object.keys(THEMES)[0]].accent}`:'none',
                  }} />
                ))}
                <div style={{ position:'absolute', inset:0, boxShadow:'0 0 0 2000px rgba(0,0,0,0.45)', borderRadius:2 }} />
              </div>
              <div style={{ marginTop:14, background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:12, padding:'5px 14px', borderRadius:20, letterSpacing:1 }}>
                {scanStatus}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
            <button onClick={() => { stopCamera(); setMode('manual'); }}
              style={{ background:t.accentSoft, border:`1px solid ${t.accent}`, borderRadius:10, padding:'11px 20px', color:t.accent, fontWeight:600, fontSize:14 }}>
              Enter Manually
            </button>
            <button onClick={() => { stopCamera(); setMode('idle'); }} style={btnStyle(t)}>Cancel</button>
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:20 }}>
          <label style={{ fontSize:13, color:t.textMuted, display:'block', marginBottom:8 }}>Barcode / ISBN / UPC / EAN</label>
          <input autoFocus value={manualInput} onChange={e => setManualInput(e.target.value)} onKeyDown={e => e.key==='Enter' && doLookup(manualInput.trim())}
            placeholder="e.g. 9780785139300"
            style={{ width:'100%', background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'10px 14px', color:t.text, fontSize:14, marginBottom:12, outline:'none' }} />
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => doLookup(manualInput.trim())} disabled={!manualInput.trim()}
              style={{ flex:1, background:t.accent, border:'none', borderRadius:8, padding:10, color:'#fff', fontWeight:600, fontSize:14, opacity:manualInput.trim()?1:0.5 }}>
              Look Up
            </button>
            <button onClick={() => setMode('idle')} style={btnStyle(t)}>Cancel</button>
          </div>
        </div>
      )}

      {mode === 'result' && (
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:14, padding:20 }}>
          {loading && <div style={{ textAlign:'center', padding:'2rem' }}><Spinner color={t.accent} /><div style={{ marginTop:10, fontSize:13, color:t.textMuted }}>Querying APIs…</div></div>}

          {!loading && results && results[0]?.error && (
            <div style={{ textAlign:'center', padding:'1.5rem' }}>
              <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
              <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>No result found</div>
              <div style={{ fontSize:13, color:t.textMuted, marginBottom:16 }}>Code: <code style={{ color:t.accent }}>{results[0].barcode}</code></div>
              <button onClick={() => { setResults(null); setMode('manual'); setManualInput(barcode); }} style={{ background:t.accent, border:'none', borderRadius:8, padding:'10px 20px', color:'#fff', fontWeight:600, fontSize:14 }}>Try Again</button>
            </div>
          )}

          {!loading && results && results[0] && !results[0].error && results.map((r, i) => (
            <div key={i} style={{ marginBottom: i < results.length-1 ? 16 : 0 }}>
              <LookupResultCard t={t} comic={r} />
              <ProfileSelector t={t} profiles={profiles} value={targetId} onChange={setTargetId} />
              <button onClick={() => handleAdd(r)} disabled={adding}
                style={{ width:'100%', background:t.accent, border:'none', borderRadius:8, padding:11, color:'#fff', fontWeight:600, fontSize:14, marginTop:10, opacity:adding?0.7:1 }}>
                {adding ? '…' : '✓ Add to Collection'}
              </button>
            </div>
          ))}

          {!loading && <button onClick={() => { setResults(null); setBarcode(''); setManualInput(''); setMode('idle'); }} style={{ ...btnStyle(t), width:'100%', marginTop:10, justifyContent:'center' }}>New Scan</button>}
        </div>
      )}
    </div>
  );
}

// ── Lookup ────────────────────────────────────────────────────
function LookupScreen({ t, activeProfile, profiles, setScreen, updateProfileCount, notify }) {
  const [q, setQ]             = useState('');
  const [source, setSource]   = useState('comicvine');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [targetId, setTargetId] = useState(activeProfile?.id || profiles[0]?.id || '');
  const [adding, setAdding]   = useState(false);

  const handleSearch = async () => {
    if (!q.trim()) return;
    setLoading(true); setResults([]); setSelected(null);
    try {
      const data = await lookup.search(q, source);
      setResults(Array.isArray(data) ? data : [data]);
      if (!data.length) notify('No results found', 'info');
    } catch (e) { notify(e.message, 'error'); }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      await comicsApi.add({ ...selected, profile_id: parseInt(targetId) });
      updateProfileCount(parseInt(targetId), 1);
      notify(`"${selected.title}" added ✓`, 'success');
      setSelected(null);
    } catch (e) { notify(e.message, 'error'); }
    setAdding(false);
  };

  const tabs = [
    { k:'comicvine',   label:'Comic Vine' },
    { k:'googlebooks', label:'Google Books' },
    { k:'openlibrary', label:'Open Library' },
  ];

  return (
    <div className="fade-in" style={{ maxWidth:700, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <header style={{ display:'flex', alignItems:'center', gap:12, marginBottom:22 }}>
        <button onClick={() => setScreen(activeProfile ? 'profile' : 'home')} style={btnStyle(t)}>← Back</button>
        <h1 style={{ fontWeight:700, fontSize:20 }}>Search & Lookup</h1>
      </header>

      <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
        {tabs.map(tab => (
          <button key={tab.k} onClick={() => { setSource(tab.k); setResults([]); setSelected(null); }}
            style={{ background:source===tab.k?t.accent:t.surface, border:`1px solid ${source===tab.k?t.accent:t.border}`, borderRadius:8, padding:'7px 14px', color:source===tab.k?'#fff':t.textMuted, fontSize:13, fontWeight:source===tab.k?600:400 }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:18 }}>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key==='Enter' && handleSearch()}
          placeholder={source==='comicvine' ? 'e.g. Amazing Spider-Man, Batman…' : 'Title, author, or ISBN…'}
          style={{ flex:1, background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, padding:'11px 16px', color:t.text, fontSize:14, outline:'none' }} />
        <button onClick={handleSearch} disabled={loading || !q.trim()}
          style={{ background:t.accent, border:'none', borderRadius:10, padding:'11px 22px', color:'#fff', fontWeight:600, fontSize:14, opacity:loading||!q.trim()?0.6:1, display:'flex', alignItems:'center', gap:8 }}>
          {loading ? <Spinner color="#fff" /> : 'Search'}
        </button>
      </div>

      {results.length > 0 && <div style={{ fontSize:13, color:t.textMuted, marginBottom:10 }}>{results.length} result{results.length!==1?'s':''}</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
        {results.map((r, i) => (
          <div key={i} onClick={() => setSelected(selected?.id===r.id ? null : r)}
            style={{ background:selected?.id===r.id?t.accentSoft:t.surface, border:`1px solid ${selected?.id===r.id?t.accent:t.border}`, borderRadius:10, padding:'12px 16px', cursor:'pointer', transition:'all 0.1s' }}>
            <LookupResultCard t={t} comic={r} compact />
          </div>
        ))}
      </div>

      {selected && (
        <div style={{ background:t.surface, border:`1.5px solid ${t.accent}`, borderRadius:12, padding:16, animation:'slideDown 0.2s ease' }}>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:10 }}>Add "{selected.title}" to:</div>
          <ProfileSelector t={t} profiles={profiles} value={targetId} onChange={setTargetId} />
          <button onClick={handleAdd} disabled={adding}
            style={{ width:'100%', background:t.accent, border:'none', borderRadius:8, padding:11, color:'#fff', fontWeight:600, fontSize:14, marginTop:10, opacity:adding?0.7:1 }}>
            {adding ? '…' : 'Add to Collection'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────
function SettingsScreen({ t, currentUser, themeKey, setThemeKey, onLogout, setScreen, notify, showFinancials, toggleShowFinancials }) {
  const [keys, setKeys]     = useState({ comicvine:'', googlebooks:'' });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState([]);

  useEffect(() => {
    lookup.keyStatus().then(d => setStatus(d.configured || [])).catch(() => {});
  }, []);

  const saveKeys = async () => {
    setSaving(true);
    try {
      await lookup.saveKeys(keys);
      notify('API keys saved', 'success');
      const d = await lookup.keyStatus();
      setStatus(d.configured || []);
      setKeys({ comicvine:'', googlebooks:'' });
    } catch (e) { notify(e.message, 'error'); }
    setSaving(false);
  };

  const apiFields = [
    { k:'comicvine',   label:'Comic Vine API Key',   hint:'Free key at comicvine.gamespot.com/api — required for comic search' },
    { k:'googlebooks', label:'Google Books API Key', hint:'Optional — console.cloud.google.com — increases daily limit' },
  ];

  return (
    <div className="fade-in" style={{ maxWidth:600, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <header style={{ display:'flex', alignItems:'center', gap:12, marginBottom:28 }}>
        <button onClick={() => setScreen('home')} style={btnStyle(t)}>← Back</button>
        <h1 style={{ fontWeight:700, fontSize:20 }}>Settings</h1>
      </header>

      {/* Account */}
      <Section t={t} label="Account">
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:16, display:'flex', alignItems:'center', gap:14 }}>
          {currentUser?.avatar_url
            ? <img src={currentUser.avatar_url} alt="" style={{ width:44, height:44, borderRadius:'50%', flexShrink:0 }} />
            : <div style={{ width:44, height:44, borderRadius:'50%', background:t.accentSoft, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                {currentUser?.display_name?.[0] || '?'}
              </div>}
          <div>
            <div style={{ fontWeight:600, fontSize:15 }}>{currentUser?.display_name}</div>
            <div style={{ fontSize:13, color:t.textMuted }}>{currentUser?.email}</div>
            <div style={{ fontSize:11, color:t.accent, marginTop:2, textTransform:'capitalize' }}>via {currentUser?.provider}</div>
          </div>
        </div>
      </Section>

      {/* Theme */}
      <Section t={t} label="Theme">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(90px,1fr))', gap:8 }}>
          {Object.entries(THEMES).map(([k, th]) => (
            <button key={k} onClick={() => setThemeKey(k)}
              style={{ background:th.bg, border:`2px solid ${themeKey===k?th.accent:th.border}`, borderRadius:10, padding:'12px 8px', cursor:'pointer', textAlign:'center' }}>
              <div style={{ fontSize:18, marginBottom:4 }}>{th.icon}</div>
              <div style={{ fontSize:11, fontWeight:600, color:th.text }}>{th.name}</div>
              <div style={{ width:20, height:3, background:th.accent, borderRadius:2, margin:'5px auto 0' }} />
            </button>
          ))}
        </div>
      </Section>

      {/* Display preferences */}
      <Section t={t} label="Display">
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:16 }}>
          <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:500, color:t.text, marginBottom:2 }}>Show financial values</div>
              <div style={{ fontSize:12, color:t.textMuted }}>Display Invested, Sold, and P&L amounts on the dashboard and profile pages</div>
            </div>
            <input type="checkbox" checked={showFinancials} onChange={e => toggleShowFinancials(e.target.checked)}
              style={{ width:38, height:22, accentColor:t.accent, flexShrink:0, marginLeft:14 }} />
          </label>
        </div>
      </Section>

      {/* API keys */}
      <Section t={t} label="API Keys">
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden', marginBottom:8 }}>
          {apiFields.map((f, i) => (
            <div key={f.k} style={{ padding:'13px 16px', borderBottom: i<apiFields.length-1?`1px solid ${t.border}`:'none' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <label style={{ fontSize:13, fontWeight:500 }}>{f.label}</label>
                {status.includes(f.k) && <span style={{ fontSize:10, color:'#16a34a', fontWeight:600 }}>✓ CONFIGURED</span>}
              </div>
              <input type="password" placeholder={f.hint} value={keys[f.k]} onChange={e => setKeys(prev => ({ ...prev, [f.k]: e.target.value }))}
                style={{ width:'100%', background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 12px', color:t.text, fontSize:13, outline:'none' }} />
            </div>
          ))}
        </div>
        <button onClick={saveKeys} disabled={saving || Object.values(keys).every(v => !v.trim())}
          style={{ background:t.accent, border:'none', borderRadius:8, padding:'10px 20px', color:'#fff', fontWeight:600, fontSize:14, width:'100%', opacity:saving?0.7:1 }}>
          {saving ? 'Saving…' : 'Save API Keys'}
        </button>
        <div style={{ fontSize:11, color:t.textMuted, marginTop:8 }}>Keys are AES-256 encrypted before storage. Never exposed to the browser.</div>
      </Section>

      {/* Sign out */}
      <Section t={t} label="Danger Zone" danger>
        <button onClick={() => { if(window.confirm('Sign out?')) onLogout(); }}
          style={{ background:'#7f1d1d22', border:'1.5px solid #ef4444', borderRadius:10, padding:'11px 24px', color:'#ef4444', fontWeight:600, fontSize:14, width:'100%' }}>
          Sign Out
        </button>
      </Section>
    </div>
  );
}

// ── Import / Export ───────────────────────────────────────────
function ImportExportScreen({ t, activeProfile, profiles, setScreen, updateProfileCount, notify }) {
  const [tab, setTab]               = useState('export'); // export | import
  const [exporting, setExporting]   = useState(false);
  const [importing, setImporting]   = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview]       = useState(null);   // parsed rows before import
  const [results, setResults]       = useState(null);   // after import
  const [targetId, setTargetId]     = useState(activeProfile?.id || profiles[0]?.id || '');
  const fileRef = useRef(null);

  // ── Template download ────────────────────────────────────────
  const downloadTemplate = () => {
    const headers = [
      'title', 'series', 'issue_number', 'publisher', 'year', 'barcode',
      'isbn', 'upc', 'condition_grade', 'personal_notes', 'is_read',
      'purchase_price', 'purchase_date', 'purchase_venue',
      'sold_price', 'sold_date', 'sold_venue',
      'writers', 'artists', 'colorists', 'letterers', 'cover_artists', 'editors',
    ];
    const example = [
      'Amazing Spider-Man', 'Amazing Spider-Man', '300', 'Marvel Comics', '1988',
      '75960600974430011', '', '', 'VF/NM 9.0', 'First full Venom appearance', 'Yes',
      '125.00', '2024-03-15', 'eBay',
      '', '', '',
      'David Michelinie', 'Todd McFarlane', 'Bob Sharen', 'Rick Parker', 'Todd McFarlane', 'Jim Salicrup',
    ];
    const csv = '\uFEFF' + headers.join(',') + '\n' + example.map(v => `"${v}"`).join(',') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'SlabVault_Import_Template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleExport = async () => {
    if (!targetId) return;
    setExporting(true);
    try {
      const csv = await importExport.exportCsv(parseInt(targetId));
      const profile = profiles.find(p => p.id == targetId);
      const safeName = (profile?.name || 'collection').replace(/[^a-z0-9]/gi, '_');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `SlabVault_${safeName}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      notify('Export downloaded ✓', 'success');
    } catch (e) { notify('Export failed: ' + e.message, 'error'); }
    setExporting(false);
  };

  // ── Parse CSV file ──────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setResults(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const rows = parseCsv(text);
      setPreview(rows);
    };
    reader.readAsText(file, 'UTF-8');
  };

  // ── Run import ──────────────────────────────────────────────
  const handleImport = async () => {
    if (!preview?.length) return;
    setImporting(true);
    setResults(null);
    try {
      const data = await importExport.importRows(parseInt(targetId), preview);
      setResults(data);
      updateProfileCount(parseInt(targetId), data.added);
      notify(`Import complete — ${data.added} added, ${data.skipped} skipped`, 'success');
      setPreview(null);
      setImportFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) { notify('Import failed: ' + e.message, 'error'); }
    setImporting(false);
  };

  return (
    <div className="fade-in" style={{ maxWidth:700, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <header style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button onClick={() => setScreen(activeProfile ? 'profile' : 'home')} style={btnStyle(t)}>← Back</button>
        <h1 style={{ fontWeight:700, fontSize:20 }}>Import / Export</h1>
      </header>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:20 }}>
        {[['export','📤 Export'],['import','📥 Import']].map(([k,l]) => (
          <button key={k} onClick={() => { setTab(k); setPreview(null); setResults(null); }}
            style={{ background:tab===k?t.accent:t.surface, border:`1px solid ${tab===k?t.accent:t.border}`, borderRadius:8, padding:'8px 20px', color:tab===k?'#fff':t.textMuted, fontWeight:tab===k?600:400, fontSize:14 }}>
            {l}
          </button>
        ))}
      </div>

      {/* Profile selector */}
      <div style={{ marginBottom:20 }}>
        <label style={{ fontSize:12, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:1, display:'block', marginBottom:6 }}>
          Profile
        </label>
        <select value={targetId} onChange={e => setTargetId(e.target.value)}
          style={{ width:'100%', background:t.surface, border:`1px solid ${t.border}`, borderRadius:8, padding:'10px 14px', color:t.text, fontSize:14, outline:'none' }}>
          {profiles.map(p => <option key={p.id} value={p.id}>{p.name} ({p.comic_count || 0} comics)</option>)}
        </select>
      </div>

      {/* ── Export tab ── */}
      {tab === 'export' && (
        <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:20, display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>Export to CSV</div>
            <div style={{ fontSize:13, color:t.textMuted, lineHeight:1.7 }}>
              Downloads all comics from the selected profile as a CSV file. Includes every field — title, series, issue, publisher, year, barcodes, condition grade, notes, and more. Compatible with Excel, Google Sheets, and any spreadsheet app.
            </div>
          </div>
          <button onClick={handleExport} disabled={exporting || !targetId}
            style={{ background:t.accent, border:'none', borderRadius:8, padding:'12px 20px', color:'#fff', fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:10, justifyContent:'center', opacity:exporting?0.7:1 }}>
            {exporting ? <><Spinner color="#fff" /> Exporting…</> : '📤 Download CSV'}
          </button>
        </div>
      )}

      {/* ── Import tab ── */}
      {tab === 'import' && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* Instructions */}
          <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:16 }}>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:8 }}>CSV Format</div>
            <div style={{ fontSize:12, color:t.textMuted, lineHeight:1.8 }}>
              Your CSV must have a header row. Supported column names:
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:10 }}>
              {['title','series','issue_number','publisher','year','barcode','isbn','upc','condition_grade','personal_notes','is_read','purchase_price','purchase_date','purchase_venue','sold_price','sold_date','sold_venue','writers','artists','colorists','letterers','cover_artists','editors'].map(col => (
                <span key={col} style={{ background:t.surfaceAlt, color:t.accent, fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:5, fontFamily:'monospace' }}>{col}</span>
              ))}
            </div>
            <div style={{ fontSize:12, color:t.textMuted, marginTop:10, lineHeight:1.7 }}>
              Only <strong style={{ color:t.text }}>title</strong> is required. The importer will try to look up each comic via Comic Vine and Google Books to fill in missing metadata automatically.
            </div>
            <button onClick={downloadTemplate}
              style={{ marginTop:12, background:t.accentSoft, border:`1px solid ${t.accent}`, borderRadius:8, padding:'8px 16px', color:t.accent, fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
              📄 Download blank template
            </button>
          </div>

          {/* File picker */}
          <div style={{ background:t.surface, border:`1px dashed ${t.border}`, borderRadius:12, padding:20, textAlign:'center' }}
            onClick={() => fileRef.current?.click()}>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFileChange} style={{ display:'none' }} />
            <div style={{ fontSize:32, marginBottom:8 }}>📁</div>
            <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>
              {importFile ? importFile.name : 'Click to select CSV file'}
            </div>
            <div style={{ fontSize:12, color:t.textMuted }}>
              {preview ? `${preview.length} rows ready to import` : 'Max 500 rows per import'}
            </div>
          </div>

          {/* Preview table */}
          {preview && preview.length > 0 && !results && (
            <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', borderBottom:`1px solid ${t.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{preview.length} rows detected</div>
                <div style={{ fontSize:12, color:t.textMuted }}>Showing first 5</div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:t.surfaceAlt }}>
                      {Object.keys(preview[0]).slice(0,6).map(k => (
                        <th key={k} style={{ padding:'8px 12px', textAlign:'left', color:t.textMuted, fontWeight:600, whiteSpace:'nowrap' }}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0,5).map((row, i) => (
                      <tr key={i} style={{ borderTop:`1px solid ${t.border}` }}>
                        {Object.values(row).slice(0,6).map((v, j) => (
                          <td key={j} style={{ padding:'8px 12px', color:t.text, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{String(v || '')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:'12px 16px', borderTop:`1px solid ${t.border}`, display:'flex', gap:10 }}>
                <button onClick={handleImport} disabled={importing}
                  style={{ flex:1, background:t.accent, border:'none', borderRadius:8, padding:'11px', color:'#fff', fontWeight:600, fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:importing?0.7:1 }}>
                  {importing ? <><Spinner color="#fff" /> Importing…</> : `📥 Import ${preview.length} comics`}
                </button>
                <button onClick={() => { setPreview(null); setImportFile(null); if(fileRef.current) fileRef.current.value=''; }} style={btnStyle(t)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {results && (
            <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'16px', display:'flex', gap:16, flexWrap:'wrap', borderBottom:`1px solid ${t.border}` }}>
                {[
                  { label:'Added',   val:results.added,   color:'#16a34a' },
                  { label:'Skipped', val:results.skipped, color:t.textMuted },
                  { label:'Total',   val:results.total,   color:t.text },
                ].map(s => (
                  <div key={s.label} style={{ textAlign:'center' }}>
                    <div style={{ fontSize:24, fontWeight:700, color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:12, color:t.textMuted }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ maxHeight:300, overflowY:'auto' }}>
                {results.results.map((r, i) => (
                  <div key={i} style={{ padding:'8px 16px', borderBottom:`1px solid ${t.border}`, display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
                    <span style={{ color: r.status==='added'?'#16a34a':'#dc2626', fontSize:16 }}>
                      {r.status==='added' ? '✓' : '✕'}
                    </span>
                    <span style={{ flex:1, color:t.text }}>Row {r.row}: {r.title || '—'}</span>
                    <span style={{ color:t.textMuted, fontSize:11 }}>{r.source || r.reason || ''}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding:'12px 16px' }}>
                <button onClick={() => setResults(null)} style={{ ...btnStyle(t), width:'100%', justifyContent:'center' }}>
                  Import Another File
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── CSV parser ────────────────────────────────────────────────
function parseCsv(text) {
  // Strip UTF-8 BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const vals = parseCsvLine(lines[i]);
    if (vals.every(v => !v.trim())) continue;
    const row = {};
    headers.forEach((h, j) => { row[h] = vals[j]?.trim() || ''; });
    rows.push(row);
  }
  return rows;
}

function parseCsvLine(line) {
  const result = [];
  let cur = '', inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuote && line[i+1] === '"') { cur += '"'; i++; }
      else inQuote = !inQuote;
    } else if (ch === ',' && !inQuote) {
      result.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur);
  return result;
}
// ── Dashboard ─────────────────────────────────────────────────
// ALL_COLUMNS defines every possible column with label, field key,
// width hint, and whether it's visible by default
// ── CGC-style condition grade scale ───────────────────────────
const GRADES = ['', 'MT 10', 'NM/MT 9.8', 'NM+ 9.6', 'NM 9.4', 'NM- 9.2', 'VF/NM 9.0',
  'VF+ 8.5', 'VF 8.0', 'VF- 7.5', 'FN/VF 7.0', 'FN+ 6.5', 'FN 6.0', 'FN- 5.5',
  'VG/FN 5.0', 'VG+ 4.5', 'VG 4.0', 'VG- 3.5', 'GD/VG 3.0', 'GD+ 2.5', 'GD 2.0',
  'GD- 1.8', 'FR/GD 1.5', 'FR 1.0', 'PR 0.5'];

const ALL_COLUMNS = [
  { key:'cover',          label:'',              width:52,  default:true,  type:'img' },
  { key:'title',          label:'Title',         width:200, default:true,  type:'text', sortable:true },
  { key:'issue_number',   label:'Issue',         width:70,  default:true,  type:'text', sortable:true },
  { key:'series',         label:'Series',        width:160, default:false, type:'text', sortable:true },
  { key:'publisher',      label:'Publisher',     width:130, default:true,  type:'text', sortable:true },
  { key:'publish_year',   label:'Year',          width:70,  default:true,  type:'text', sortable:true },
  { key:'condition_grade',label:'Grade',         width:100, default:true,  type:'grade', sortable:true },
  { key:'writers',        label:'Writer(s)',     width:150, default:false, type:'text' },
  { key:'artists',        label:'Artist(s)',     width:150, default:false, type:'text' },
  { key:'story_arcs',     label:'Story Arc',     width:150, default:false, type:'text' },
  { key:'characters',     label:'Characters',    width:150, default:false, type:'text' },
  { key:'first_appearances',label:'1st App.',    width:150, default:false, type:'text' },
  { key:'page_count',     label:'Pages',         width:70,  default:false, type:'text' },
  { key:'physical_format',label:'Format',        width:100, default:false, type:'text' },
  { key:'language',       label:'Language',      width:80,  default:false, type:'text' },
  { key:'google_rating',  label:'Rating',        width:80,  default:false, type:'rating' },
  { key:'source_api',     label:'Source',        width:100, default:false, type:'badge' },
  { key:'is_read',        label:'Read',          width:60,  default:false, type:'bool' },
  { key:'purchase_price', label:'Cost',          width:90,  default:true,  type:'money', sortable:true },
  { key:'purchase_date',  label:'Bought',        width:100, default:false, type:'date' },
  { key:'purchase_venue', label:'Bought From',   width:120, default:false, type:'text' },
  { key:'sold_price',     label:'Sold',          width:90,  default:true,  type:'money', sortable:true },
  { key:'sold_date',      label:'Sold On',       width:100, default:false, type:'date' },
  { key:'sold_venue',     label:'Sold Via',      width:120, default:false, type:'text' },
  { key:'pl',             label:'P&L',           width:90,  default:true,  type:'pl' },
  { key:'added_at',       label:'Added',         width:100, default:false, type:'date', sortable:true },
  { key:'barcode',        label:'Barcode',       width:150, default:false, type:'mono' },
  { key:'isbn',           label:'ISBN',          width:130, default:false, type:'mono' },
  { key:'comicvine_id',   label:'CV ID',         width:90,  default:false, type:'mono' },
];

function DashboardScreen({ t, activeProfile, profiles, setScreen, updateProfileCount, notify, showFinancials }) {
  const [comics, setComics]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [sort, setSort]             = useState('added_at');
  const [dir, setDir]               = useState('desc');
  const [selectedComic, setSelected]= useState(null);
  const [showColPicker, setColPicker] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue]   = useState('');
  const [saving, setSaving]         = useState(false);
  const [zoomedCover, setZoomedCover] = useState(null);

  // Column visibility — load from localStorage
  const storageKey = `sv_cols_${activeProfile?.id || 'default'}`;
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return ALL_COLUMNS.filter(c => c.default).map(c => c.key);
  });

  const saveVisibleCols = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem(storageKey, JSON.stringify(cols)); } catch {}
  };

  const PER_PAGE = 50;

  const fetchComics = useCallback(async (pg = 1, q = '', s = sort, d = dir) => {
    if (!activeProfile) return;
    setLoading(true);
    try {
      const res = await comicsApi.dashboard(activeProfile.id, {
        page: pg, limit: PER_PAGE, ...(q ? { search: q } : {}),
        sort: s, dir: d,
      });
      setComics(res.comics);
      setTotal(res.total);
      setTotalPages(res.pages);
      setPage(pg);
    } catch (e) { notify(e.message, 'error'); }
    setLoading(false);
  }, [activeProfile, sort, dir]);

  useEffect(() => { fetchComics(); }, [fetchComics]);

  const handleSort = (col) => {
    const newDir = sort === col && dir === 'desc' ? 'asc' : 'desc';
    setSort(col); setDir(newDir);
    fetchComics(1, search, col, newDir);
  };

  const handleSearch = (q) => { setSearch(q); fetchComics(1, q); };

  const removeComic = async (id) => {
    try {
      await comicsApi.remove(id);
      setComics(prev => prev.filter(c => c.id !== id));
      updateProfileCount(activeProfile.id, -1);
      if (selectedComic?.id === id) setSelected(null);
      notify('Removed', 'info');
    } catch (e) { notify(e.message, 'error'); }
  };

  const startEdit = (field, value) => { setEditingField(field); setEditValue(value ?? ''); };

  const saveEdit = async () => {
    if (!selectedComic || !editingField) return;
    setSaving(true);
    try {
      const updated = await comicsApi.update(selectedComic.id, { [editingField]: editValue || null });
      setComics(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
      setSelected(prev => ({ ...prev, ...updated }));
      setEditingField(null);
      notify('Saved ✓', 'success');
    } catch (e) { notify(e.message, 'error'); }
    setSaving(false);
  };

  // P&L summary
  const totalCost = comics.reduce((s, c) => s + (c.purchase_price ? parseFloat(c.purchase_price) : 0), 0);
  const totalSold = comics.reduce((s, c) => s + (c.sold_price ? parseFloat(c.sold_price) : 0), 0);
  const totalPL   = totalSold - comics.filter(c => c.sold_price && c.purchase_price)
                                      .reduce((s, c) => s + parseFloat(c.purchase_price), 0);

  const FINANCIAL_COLS = ['purchase_price','purchase_date','purchase_venue','sold_price','sold_date','sold_venue','pl'];
  const displayCols = ALL_COLUMNS.filter(c => visibleCols.includes(c.key) && (showFinancials || !FINANCIAL_COLS.includes(c.key)));

  const gradeColor = (g) => {
    if (!g) return t.textMuted;
    const n = parseFloat(g);
    if (n >= 9.0) return '#16a34a';
    if (n >= 7.0) return '#2563eb';
    if (n >= 4.0) return '#d97706';
    return '#dc2626';
  };

  const renderCell = (comic, col) => {
    switch (col.type) {
      case 'img':
        return comic.cover_url
          ? <img src={comic.cover_url} alt="" onClick={(e) => { e.stopPropagation(); setZoomedCover(comic); }}
              style={{ width:36, height:50, objectFit:'cover', borderRadius:3, display:'block', cursor:'zoom-in' }} onError={e => e.target.style.display='none'} />
          : <div style={{ width:36, height:50, background:t.surfaceAlt, borderRadius:3, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>📖</div>;
      case 'grade':
        return comic.condition_grade
          ? <span style={{ fontSize:11, fontWeight:700, color:gradeColor(comic.condition_grade), background:gradeColor(comic.condition_grade)+'18', padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap' }}>{comic.condition_grade}</span>
          : <span style={{ color:t.textMuted, fontSize:11 }}>—</span>;
      case 'money':
        return comic[col.key]
          ? <span style={{ fontWeight:600, fontSize:12 }}>${parseFloat(comic[col.key]).toFixed(2)}</span>
          : <span style={{ color:t.textMuted, fontSize:11 }}>—</span>;
      case 'pl': {
        if (!comic.purchase_price && !comic.sold_price) return <span style={{ color:t.textMuted, fontSize:11 }}>—</span>;
        const pl = (comic.sold_price ? parseFloat(comic.sold_price) : 0) - (comic.purchase_price ? parseFloat(comic.purchase_price) : 0);
        return <span style={{ fontWeight:700, fontSize:12, color: pl >= 0 ? '#16a34a' : '#dc2626' }}>{pl >= 0 ? '+' : ''}${pl.toFixed(2)}</span>;
      }
      case 'bool':
        return <span style={{ color: comic[col.key] ? '#16a34a' : t.textMuted, fontSize:11 }}>{comic[col.key] ? '✓' : '—'}</span>;
      case 'badge':
        return comic[col.key]
          ? <span style={{ background:t.accentSoft, color:t.accent, fontSize:10, fontWeight:600, padding:'2px 6px', borderRadius:4 }}>{comic[col.key]}</span>
          : null;
      case 'rating':
        return comic[col.key] ? <span style={{ fontSize:11 }}>⭐ {comic[col.key]}</span> : null;
      case 'date':
        return comic[col.key] ? <span style={{ fontSize:11, color:t.textMuted }}>{comic[col.key]?.slice(0,10)}</span> : null;
      case 'mono':
        return comic[col.key] ? <span style={{ fontFamily:'monospace', fontSize:10, color:t.textMuted }}>{comic[col.key]}</span> : null;
      default:
        return <span style={{ fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block', maxWidth:col.width - 16 }}>{comic[col.key] || '—'}</span>;
    }
  };

  const EDITABLE_SALES = [
    { key:'purchase_price', label:'Price Paid ($)',     type:'number', money:true },
    { key:'purchase_date',  label:'Date Purchased',     type:'date' },
    { key:'purchase_venue', label:'Where Purchased',    type:'text' },
    { key:'sold_price',     label:'Sold For ($)',        type:'number', money:true },
    { key:'sold_date',      label:'Date Sold',           type:'date' },
    { key:'sold_venue',     label:'Where Sold',          type:'text' },
  ];

  const EDITABLE_DETAILS = [
    { key:'issue_number',    label:'Issue Number',  type:'text' },
    { key:'publisher',       label:'Publisher',     type:'text' },
    { key:'publish_year',    label:'Year',          type:'number' },
    { key:'publish_date',    label:'Publish Date',  type:'date' },
    { key:'condition_grade', label:'Condition/Grade', type:'grade' },
    { key:'isbn',            label:'ISBN',          type:'text' },
    { key:'barcode',         label:'Barcode',       type:'text' },
    { key:'upc',             label:'UPC',           type:'text' },
  ];

  if (!activeProfile) { setScreen('home'); return null; }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:t.bg, overflow:'hidden' }}>

      {/* Top bar */}
      <div style={{ background:t.surface, borderBottom:`1px solid ${t.border}`, padding:'10px 16px', display:'flex', alignItems:'center', gap:10, flexShrink:0, flexWrap:'wrap' }}>
        <div style={{ fontWeight:700, fontSize:15, color:t.text }}>{activeProfile?.name || 'Dashboard'}</div>
        <div style={{ fontSize:12, color:t.textMuted }}>{total} comics</div>

        {/* Summary stats */}
        {showFinancials && totalCost > 0 && (
          <div style={{ display:'flex', gap:8, marginLeft:8, flexWrap:'wrap' }}>
            <StatPill label="Invested" value={`$${totalCost.toFixed(2)}`} color={t.textMuted} t={t} />
            {totalSold > 0 && <StatPill label="Sold" value={`$${totalSold.toFixed(2)}`} color="#16a34a" t={t} />}
            {totalSold > 0 && <StatPill label="P&L" value={`${totalPL >= 0?'+':''}$${totalPL.toFixed(2)}`} color={totalPL >= 0 ? '#16a34a' : '#dc2626'} t={t} />}
          </div>
        )}

        <div style={{ flex:1 }} />

        {/* Search */}
        <input value={search} onChange={e => handleSearch(e.target.value)}
          placeholder="Search…"
          style={{ background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'6px 12px', color:t.text, fontSize:13, outline:'none', width:180 }} />

        {/* Column picker toggle */}
        <button onClick={() => setColPicker(!showColPicker)}
          style={{ ...btnStyle(t), background: showColPicker ? t.accentSoft : t.surfaceAlt, color: showColPicker ? t.accent : t.textMuted }}>
          ⚙ Columns
        </button>
      </div>

      {/* Column picker dropdown */}
      {showColPicker && (
        <div style={{ background:t.surface, borderBottom:`1px solid ${t.border}`, padding:'10px 16px', display:'flex', gap:6, flexWrap:'wrap', flexShrink:0 }}>
          {ALL_COLUMNS.filter(c => c.key !== 'cover' && (showFinancials || !FINANCIAL_COLS.includes(c.key))).map(col => (
            <button key={col.key}
              onClick={() => {
                const next = visibleCols.includes(col.key)
                  ? visibleCols.filter(k => k !== col.key)
                  : [...visibleCols, col.key];
                saveVisibleCols(next);
              }}
              style={{ background: visibleCols.includes(col.key) ? t.accent : t.surfaceAlt, border:`1px solid ${visibleCols.includes(col.key) ? t.accent : t.border}`, borderRadius:6, padding:'4px 10px', color: visibleCols.includes(col.key) ? '#fff' : t.textMuted, fontSize:12, cursor:'pointer' }}>
              {col.label || col.key}
            </button>
          ))}
          <button onClick={() => saveVisibleCols(ALL_COLUMNS.filter(c=>c.default).map(c=>c.key))}
            style={{ ...btnStyle(t), fontSize:12, marginLeft:'auto' }}>Reset</button>
        </div>
      )}

      {/* Main area: grid + slide-out panel */}
      <div style={{ flex:1, display:'flex', overflow:'hidden' }}>

        {/* Data grid */}
        <div style={{ flex:1, overflow:'auto', position:'relative' }}>
          {loading && (
            <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:t.bg+'cc', zIndex:10 }}>
              <Spinner color={t.accent} />
            </div>
          )}

          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, tableLayout:'fixed' }}>
            <thead style={{ position:'sticky', top:0, zIndex:5 }}>
              <tr style={{ background:t.surface, borderBottom:`1.5px solid ${t.border}` }}>
                {displayCols.map(col => (
                  <th key={col.key}
                    style={{ padding:'8px 10px', textAlign:'left', fontWeight:600, fontSize:11, color:t.textMuted, whiteSpace:'nowrap', width:col.width, cursor: col.sortable ? 'pointer' : 'default', userSelect:'none', position:'relative' }}
                    onClick={() => col.sortable && handleSort(col.key)}>
                    {col.label}
                    {col.sortable && sort === col.key && (
                      <span style={{ marginLeft:4, color:t.accent }}>{dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
                <th style={{ width:36, padding:'8px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {comics.map((comic, i) => (
                <tr key={comic.id}
                  onClick={() => setSelected(selectedComic?.id === comic.id ? null : comic)}
                  style={{ background: selectedComic?.id === comic.id ? t.accentSoft : i % 2 === 0 ? t.surface : t.bg, borderBottom:`1px solid ${t.border}`, cursor:'pointer', transition:'background 0.1s' }}
                  onMouseEnter={e => { if (selectedComic?.id !== comic.id) e.currentTarget.style.background = t.surfaceAlt; }}
                  onMouseLeave={e => { e.currentTarget.style.background = selectedComic?.id === comic.id ? t.accentSoft : i%2===0 ? t.surface : t.bg; }}>
                  {displayCols.map(col => (
                    <td key={col.key} style={{ padding:'6px 10px', overflow:'hidden', maxWidth:col.width, verticalAlign:'middle' }}>
                      {renderCell(comic, col)}
                    </td>
                  ))}
                  <td style={{ padding:'6px 6px', textAlign:'right' }}>
                    <button onClick={e => { e.stopPropagation(); if(window.confirm(`Remove "${comic.title}"?`)) removeComic(comic.id); }}
                      style={{ background:'none', border:'none', color:t.textMuted, fontSize:13, cursor:'pointer', opacity:0.5, padding:'2px 4px' }}>🗑</button>
                  </td>
                </tr>
              ))}
              {!loading && comics.length === 0 && (
                <tr><td colSpan={displayCols.length + 1} style={{ padding:'4rem', textAlign:'center', color:t.textMuted }}>
                  No comics found
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Slide-out detail panel */}
        {selectedComic && (
          <div style={{ width:360, borderLeft:`1px solid ${t.border}`, background:t.surface, overflowY:'auto', flexShrink:0, display:'flex', flexDirection:'column' }}>
            {/* Panel header */}
            <div style={{ padding:'12px 14px', borderBottom:`1px solid ${t.border}`, display:'flex', alignItems:'center', gap:10 }}>
              {selectedComic.cover_url
                ? <img src={selectedComic.cover_url} alt="" onClick={() => setZoomedCover(selectedComic)}
                    style={{ width:44, height:62, objectFit:'cover', borderRadius:5, flexShrink:0, cursor:'zoom-in' }} />
                : <div style={{ width:44, height:62, background:t.surfaceAlt, borderRadius:5, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>📖</div>}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:13, color:t.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedComic.title}</div>
                {selectedComic.issue_number && <div style={{ fontSize:12, color:t.accent }}>Issue #{selectedComic.issue_number}</div>}
                {selectedComic.publisher && <div style={{ fontSize:11, color:t.textMuted }}>{selectedComic.publisher}</div>}
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', color:t.textMuted, fontSize:18, cursor:'pointer', flexShrink:0 }}>✕</button>
            </div>

            {/* Panel body */}
            <div style={{ padding:'12px 14px', display:'flex', flexDirection:'column', gap:14, flex:1 }}>

              {/* Description / deck */}
              {(selectedComic.deck || selectedComic.description) && (
                <div>
                  {selectedComic.deck && <div style={{ fontSize:13, fontWeight:500, color:t.text, marginBottom:4 }}>{selectedComic.deck}</div>}
                  {selectedComic.description && (
                    <div style={{ fontSize:12, color:t.textMuted, lineHeight:1.6, display:'-webkit-box', WebkitLineClamp:4, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                      {selectedComic.description}
                    </div>
                  )}
                </div>
              )}

              {/* Details — editable core fields */}
              <PanelSection label="Details">
                {EDITABLE_DETAILS.map(f => (
                  <EditableRow key={f.key} t={t} field={f} value={selectedComic[f.key]}
                    editingField={editingField} editValue={editValue} setEditValue={setEditValue}
                    startEdit={startEdit} saveEdit={saveEdit} cancelEdit={() => setEditingField(null)} saving={saving} />
                ))}
                {/* Read-only supplementary info */}
                <MetaGrid t={t} rows={[
                  ['Series',    selectedComic.series],
                  ['Format',    selectedComic.physical_format],
                  ['Pages',     selectedComic.page_count],
                  ['Language',  selectedComic.language],
                  ['Source',    selectedComic.source_api],
                  ...(selectedComic.google_rating ? [['Rating', `⭐ ${selectedComic.google_rating} (${selectedComic.ratings_count} ratings)`]] : []),
                ]} />
              </PanelSection>

              {/* Creators */}
              {(selectedComic.writers || selectedComic.artists || selectedComic.colorists || selectedComic.letterers || selectedComic.cover_artists || selectedComic.editors) && (
                <PanelSection label="Creators">
                  <MetaGrid t={t} rows={[
                    ['Writer(s)',      selectedComic.writers],
                    ['Artist(s)',      selectedComic.artists],
                    ['Colorist(s)',    selectedComic.colorists],
                    ['Letterer(s)',    selectedComic.letterers],
                    ['Cover Artist',  selectedComic.cover_artists],
                    ['Editor(s)',      selectedComic.editors],
                  ]} />
                </PanelSection>
              )}

              {/* Story context */}
              {(selectedComic.story_arcs || selectedComic.characters || selectedComic.first_appearances || selectedComic.teams) && (
                <PanelSection label="Story Context">
                  <MetaGrid t={t} rows={[
                    ['Story Arc(s)',   selectedComic.story_arcs],
                    ['1st App.',       selectedComic.first_appearances],
                    ['Characters',     selectedComic.characters],
                    ['Teams',          selectedComic.teams],
                    ['Locations',      selectedComic.locations],
                  ]} />
                </PanelSection>
              )}

              {/* Identifiers */}
              <PanelSection label="Identifiers">
                <MetaGrid t={t} mono rows={[
                  ['UPC',       selectedComic.upc],
                  ['CV ID',     selectedComic.comicvine_id],
                  ['GB ID',     selectedComic.googlebooks_id],
                  ['OL ID',     selectedComic.openlibrary_id],
                  ['Goodreads', selectedComic.goodreads_id],
                ]} />
                {selectedComic.cv_site_url && (
                  <a href={selectedComic.cv_site_url} target="_blank" rel="noreferrer"
                    style={{ fontSize:11, color:t.accent, textDecoration:'none', display:'block', marginTop:6 }}>
                    View on Comic Vine ↗
                  </a>
                )}
                {selectedComic.preview_link && (
                  <a href={selectedComic.preview_link} target="_blank" rel="noreferrer"
                    style={{ fontSize:11, color:t.accent, textDecoration:'none', display:'block', marginTop:4 }}>
                    Preview on Google Books ↗
                  </a>
                )}
              </PanelSection>

              {/* Market Value — community valuation + search links */}
              {showFinancials && (
                <PanelSection label="Market Value">
                  <EditableRow t={t} field={{ key:'estimated_value', label:'Your Estimated Value ($)', type:'number', money:true }}
                    value={selectedComic.estimated_value}
                    editingField={editingField} editValue={editValue} setEditValue={setEditValue}
                    startEdit={startEdit} saveEdit={saveEdit} cancelEdit={() => setEditingField(null)} saving={saving} />
                  {selectedComic.estimated_value_date && (
                    <div style={{ fontSize:10, color:t.textMuted, marginTop:-4, marginBottom:8 }}>
                      Last updated {selectedComic.estimated_value_date}
                    </div>
                  )}

                  <CommunityValuation t={t} comic={selectedComic} />

                  <MarketSearchLinks t={t} comic={selectedComic} />
                </PanelSection>
              )}

              {/* Sales — editable, hidden if user disabled financial display */}
              {showFinancials && (
                <PanelSection label="Sales & Pricing">
                  {EDITABLE_SALES.map(f => (
                    <EditableRow key={f.key} t={t} field={f} value={selectedComic[f.key]}
                      editingField={editingField} editValue={editValue} setEditValue={setEditValue}
                      startEdit={startEdit} saveEdit={saveEdit} cancelEdit={() => setEditingField(null)} saving={saving} />
                  ))}

                  {/* P&L display */}
                  {selectedComic.purchase_price && selectedComic.sold_price && (() => {
                    const pl = parseFloat(selectedComic.sold_price) - parseFloat(selectedComic.purchase_price);
                    return (
                      <div style={{ marginTop:6, background:pl>=0?'#16a34a18':'#dc262618', borderRadius:8, padding:'8px 10px', display:'flex', justifyContent:'space-between' }}>
                        <span style={{ fontSize:12, color:t.textMuted }}>Realized P&L</span>
                        <span style={{ fontSize:14, fontWeight:700, color:pl>=0?'#16a34a':'#dc2626' }}>{pl>=0?'+':''}${pl.toFixed(2)}</span>
                      </div>
                    );
                  })()}
                </PanelSection>
              )}

              {/* Notes — editable */}
              <PanelSection label="Notes">
                {editingField === 'personal_notes' ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <textarea autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}
                      rows={4}
                      style={{ width:'100%', background:t.surfaceAlt, border:`1px solid ${t.accent}`, borderRadius:6, padding:'8px', color:t.text, fontSize:12, outline:'none', resize:'vertical', fontFamily:'inherit' }} />
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={saveEdit} disabled={saving}
                        style={{ background:t.accent, border:'none', borderRadius:6, padding:'5px 14px', color:'#fff', fontSize:12, fontWeight:600 }}>
                        {saving ? '…' : '✓ Save'}
                      </button>
                      <button onClick={() => setEditingField(null)}
                        style={{ background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:6, padding:'5px 14px', color:t.textMuted, fontSize:12 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div onClick={() => startEdit('personal_notes', selectedComic.personal_notes)}
                    style={{ fontSize:12, color: selectedComic.personal_notes ? t.text : t.textMuted, lineHeight:1.6, whiteSpace:'pre-wrap', cursor:'pointer', padding:'8px', background:t.surfaceAlt, borderRadius:6, border:`1px solid ${t.border}`, minHeight:20 }}>
                    {selectedComic.personal_notes || 'Click to add notes…'}
                  </div>
                )}
              </PanelSection>

            </div>
          </div>
        )}
      </div>

      {/* Pagination footer */}
      {totalPages > 1 && (
        <div style={{ background:t.surface, borderTop:`1px solid ${t.border}`, padding:'8px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ fontSize:12, color:t.textMuted }}>
            Showing {((page-1)*PER_PAGE)+1}–{Math.min(page*PER_PAGE, total)} of {total}
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <button disabled={page<=1} onClick={() => fetchComics(page-1, search)} style={btnStyle(t)}>← Prev</button>
            {Array.from({length:Math.min(5, totalPages)}, (_,i) => {
              const p = totalPages <= 5 ? i+1 : Math.max(1, Math.min(page-2, totalPages-4)) + i;
              return (
                <button key={p} onClick={() => fetchComics(p, search)}
                  style={{ ...btnStyle(t), background: p===page ? t.accent : t.surfaceAlt, color: p===page ? '#fff' : t.textMuted, minWidth:32, justifyContent:'center' }}>
                  {p}
                </button>
              );
            })}
            <button disabled={page>=totalPages} onClick={() => fetchComics(page+1, search)} style={btnStyle(t)}>Next →</button>
          </div>
          <div style={{ fontSize:12, color:t.textMuted }}>Page {page} of {totalPages}</div>
        </div>
      )}

      {/* Cover zoom overlay */}
      {zoomedCover && (
        <div onClick={() => setZoomedCover(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out', animation:'fadeIn 0.15s ease' }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14, maxWidth:'90vw', maxHeight:'90vh' }}>
            <img src={zoomedCover.cover_url_large || zoomedCover.cover_url} alt={zoomedCover.title}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth:'min(500px, 85vw)', maxHeight:'75vh', objectFit:'contain', borderRadius:10, boxShadow:'0 20px 60px rgba(0,0,0,0.6)', cursor:'zoom-out' }} />
            <div style={{ textAlign:'center', color:'#fff' }}>
              <div style={{ fontSize:15, fontWeight:600 }}>{zoomedCover.title}</div>
              {zoomedCover.issue_number && <div style={{ fontSize:13, color:'rgba(255,255,255,.6)', marginTop:2 }}>Issue #{zoomedCover.issue_number}</div>}
            </div>
            <button onClick={() => setZoomedCover(null)}
              style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', borderRadius:8, padding:'7px 16px', color:'#fff', fontSize:13 }}>
              ✕ Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value, color, t }) {
  return (
    <div style={{ background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'3px 10px', display:'flex', gap:6, alignItems:'baseline' }}>
      <span style={{ fontSize:10, color:t.textMuted }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:700, color }}>{value}</span>
    </div>
  );
}

function PanelSection({ label, children }) {
  return (
    <div>
      <div style={{ fontSize:10, fontWeight:600, letterSpacing:1.5, textTransform:'uppercase', color:'var(--sv-muted, #7a7570)', marginBottom:6 }}>{label}</div>
      {children}
    </div>
  );
}

function MetaGrid({ t, rows, mono }) {
  const filtered = rows.filter(([, v]) => v);
  if (!filtered.length) return null;
  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
      {filtered.map(([label, value]) => (
        <div key={label} style={{ background:t.surfaceAlt, borderRadius:6, padding:'5px 8px' }}>
          <div style={{ fontSize:9, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5 }}>{label}</div>
          <div style={{ fontSize:11, color:t.text, marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Reusable editable field row for the slide-out panel ──────
function EditableRow({ t, field, value, editingField, editValue, setEditValue, startEdit, saveEdit, cancelEdit, saving }) {
  const isEditing = editingField === field.key;
  const display = value
    ? (field.type === 'number' && field.money ? `$${parseFloat(value).toFixed(2)}` : value)
    : '—';

  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:3 }}>{field.label}</div>
      {isEditing ? (
        <div style={{ display:'flex', gap:6 }}>
          {field.type === 'grade' ? (
            <select autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
              style={{ flex:1, background:t.surfaceAlt, border:`1px solid ${t.accent}`, borderRadius:6, padding:'5px 8px', color: editValue ? gradeColorFor(editValue) : t.text, fontSize:12, fontWeight:700, outline:'none' }}>
              {GRADES.map(g => <option key={g} value={g}>{g || '— Not set —'}</option>)}
            </select>
          ) : (
            <input autoFocus type={field.type} value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
              style={{ flex:1, background:t.surfaceAlt, border:`1px solid ${t.accent}`, borderRadius:6, padding:'5px 8px', color:t.text, fontSize:12, outline:'none' }} />
          )}
          <button onClick={saveEdit} disabled={saving}
            style={{ background:t.accent, border:'none', borderRadius:6, padding:'5px 10px', color:'#fff', fontSize:12, fontWeight:600 }}>
            {saving ? '…' : '✓'}
          </button>
          <button onClick={cancelEdit}
            style={{ background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:6, padding:'5px 8px', color:t.textMuted, fontSize:12 }}>✕</button>
        </div>
      ) : (
        <div onClick={() => startEdit(field.key, value)}
          style={{ fontSize:12, color: field.type === 'grade' && value ? gradeColorFor(value) : value ? t.text : t.textMuted, fontWeight: field.type === 'grade' && value ? 700 : 400, cursor:'pointer', padding:'5px 8px', background:t.surfaceAlt, borderRadius:6, border:`1px solid ${t.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{display}</span>
          <span style={{ color:t.textMuted, fontSize:10, flexShrink:0, marginLeft:6 }}>✏</span>
        </div>
      )}
    </div>
  );
}

// ── Grade color helper (shared) ───────────────────────────────
function gradeColorFor(g) {
  if (!g) return '#7a7570';
  const n = parseFloat(g);
  if (n >= 9.0) return '#16a34a';
  if (n >= 7.0) return '#2563eb';
  if (n >= 4.0) return '#d97706';
  return '#dc2626';
}

// ── Community Valuation ───────────────────────────────────────
// Pulls aggregated estimated values, sold prices, and purchase prices
// for the same series+issue across ALL users' collections.
function CommunityValuation({ t, comic }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    comicsApi.valuation(comic.id)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [comic.id]);

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 0', fontSize:12, color:t.textMuted }}>
        <Spinner color={t.accent} /> Checking community data…
      </div>
    );
  }

  if (error || !data || data.total_points === 0) {
    return (
      <div style={{ fontSize:11, color:t.textMuted, padding:'6px 0', lineHeight:1.6 }}>
        No community valuation data yet for this issue. As more collectors record values, sold prices, and purchases for {data?.display_label || comic.series || comic.title}{data?.issue_number ? ` #${data.issue_number}` : ''}, an average will appear here.
      </div>
    );
  }

  return (
    <div style={{ background:t.surfaceAlt, borderRadius:8, padding:'10px 12px', marginTop:4, marginBottom:8 }}>
      <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:8 }}>
        Community Average · {data.total_points} data point{data.total_points !== 1 ? 's' : ''}
      </div>

      {/* Overall average */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
        <span style={{ fontSize:12, color:t.text }}>Overall</span>
        <span style={{ fontSize:18, fontWeight:700, color:t.accent }}>
          {data.overall_avg !== null ? `$${data.overall_avg.toFixed(2)}` : '—'}
        </span>
      </div>

      {/* Graded vs ungraded */}
      {(data.graded_count > 0 || data.raw_count > 0) && (
        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          {data.graded_count > 0 && (
            <div style={{ flex:1, background:t.surface, borderRadius:6, padding:'6px 8px' }}>
              <div style={{ fontSize:9, color:t.textMuted, textTransform:'uppercase', letterSpacing:0.5 }}>Graded ({data.graded_count})</div>
              <div style={{ fontSize:13, fontWeight:700, color:t.text, marginTop:1 }}>${data.graded_avg?.toFixed(2)}</div>
            </div>
          )}
          {data.raw_count > 0 && (
            <div style={{ flex:1, background:t.surface, borderRadius:6, padding:'6px 8px' }}>
              <div style={{ fontSize:9, color:t.textMuted, textTransform:'uppercase', letterSpacing:0.5 }}>Ungraded ({data.raw_count})</div>
              <div style={{ fontSize:13, fontWeight:700, color:t.text, marginTop:1 }}>${data.raw_avg?.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}

      {/* 30/60/90 day windows */}
      <div style={{ display:'flex', gap:6 }}>
        {[['30d', data.window_30], ['60d', data.window_60], ['90d', data.window_90]].map(([label, w]) => (
          <div key={label} style={{ flex:1, textAlign:'center', background:t.surface, borderRadius:6, padding:'5px 4px' }}>
            <div style={{ fontSize:9, color:t.textMuted, textTransform:'uppercase' }}>{label}</div>
            <div style={{ fontSize:12, fontWeight:600, color: w.avg !== null ? t.text : t.textMuted, marginTop:1 }}>
              {w.avg !== null ? `$${w.avg.toFixed(2)}` : '—'}
            </div>
            <div style={{ fontSize:8, color:t.textMuted }}>{w.count} pt{w.count !== 1 ? 's' : ''}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize:9, color:t.textMuted, marginTop:8, lineHeight:1.5 }}>
        Each copy contributes one value — its sold price if sold, otherwise its estimated value, otherwise its purchase price. Based on {data.display_label}{data.issue_number ? ` #${data.issue_number}` : ''} across all SlabVault collections.
      </div>
    </div>
  );
}

// ── Market Search Links ───────────────────────────────────────
// Pre-filled search links to external marketplaces — no API/scraping.
function MarketSearchLinks({ t, comic }) {
  const query = [comic.series || comic.title, comic.issue_number ? `#${comic.issue_number}` : '', 'comic']
    .filter(Boolean).join(' ');
  const q = encodeURIComponent(query);

  // LH_Sold=1&LH_Complete=1 shows sold/completed listings — real transaction prices
  const ebayBase = `https://www.ebay.com/sch/i.html?_nkw=${q}&LH_Sold=1&LH_Complete=1`;

  const links = [
    { label: 'eBay Sold',     url: ebayBase,                                                           color: '#e53238' },
    { label: 'eBay Graded',   url: `${ebayBase}&_nkw=${encodeURIComponent(query + ' CGC')}`,           color: '#c0392b' },
    { label: 'Heritage',      url: `https://comics.ha.com/c/search-results.zx?Nty=1&Ntt=${q}`,        color: '#8b5cf6' },
    { label: 'MyComicShop',   url: `https://www.mycomicshop.com/search?q=${q}`,                        color: '#2563eb' },
    { label: 'ComicLink',     url: `https://www.comiclink.com/search?q=${q}`,                          color: '#16a34a' },
  ];

  return (
    <div style={{ marginTop:4 }}>
      <div style={{ fontSize:10, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>
        Check Current Listings
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {links.map(l => (
          <a key={l.label} href={l.url} target="_blank" rel="noreferrer"
            style={{ fontSize:11, fontWeight:600, padding:'5px 10px', borderRadius:6, background:t.surfaceAlt, border:`1px solid ${t.border}`, color:t.text, textDecoration:'none', display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:l.color, display:'inline-block' }} />
            {l.label} ↗
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Enrichment Runner ─────────────────────────────────────────
function EnrichmentRunner({ t, notify }) {
  const [token, setToken]       = useState('');
  const [running, setRunning]   = useState(false);
  const [log, setLog]           = useState([]);
  const [options, setOptions]   = useState({ missing_only: true, dry_run: false });

  const run = async () => {
    if (!token.trim()) { notify('Enter your enrichment token from comics_config.php', 'error'); return; }
    setRunning(true);
    setLog([]);

    try {
      const params = new URLSearchParams({
        token:        token.trim(),
        missing_only: options.missing_only ? '1' : '0',
        dry_run:      options.dry_run ? '1' : '0',
      });
      const res = await fetch(`/api/enrich?${params}`, { credentials:'include' });
      const text = await res.text();
      setLog(text.split('\n').filter(Boolean));
      notify('Enrichment complete ✓', 'success');
    } catch (e) {
      notify('Enrichment failed: ' + e.message, 'error');
    }
    setRunning(false);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      <div>
        <label style={{ fontSize:11, color:t.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, display:'block', marginBottom:5 }}>
          Enrichment Token (from comics_config.php)
        </label>
        <input type="password" value={token} onChange={e => setToken(e.target.value)}
          placeholder="ENRICH_TOKEN value"
          style={{ width:'100%', background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:7, padding:'8px 12px', color:t.text, fontSize:13, outline:'none' }} />
      </div>

      <div style={{ display:'flex', gap:14 }}>
        {[
          { key:'missing_only', label:'Missing fields only (skip complete comics)' },
          { key:'dry_run',      label:'Dry run (preview, no saves)' },
        ].map(o => (
          <label key={o.key} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:t.text, userSelect:'none' }}>
            <input type="checkbox" checked={options[o.key]} onChange={e => setOptions(prev => ({ ...prev, [o.key]: e.target.checked }))}
              style={{ width:15, height:15, accentColor:t.accent }} />
            {o.label}
          </label>
        ))}
      </div>

      <button onClick={run} disabled={running || !token.trim()}
        style={{ background:t.accent, border:'none', borderRadius:8, padding:'10px 18px', color:'#fff', fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:8, opacity: running || !token.trim() ? 0.6 : 1 }}>
        {running ? <><Spinner color="#fff" /> Running enrichment…</> : '▶ Run Enrichment'}
      </button>

      {log.length > 0 && (
        <div style={{ background:t.bg, border:`1px solid ${t.border}`, borderRadius:8, padding:10, maxHeight:300, overflowY:'auto', fontFamily:'monospace', fontSize:11, lineHeight:1.7 }}>
          {log.map((line, i) => (
            <div key={i} style={{ color: line.startsWith('  ✓') ? '#16a34a' : line.startsWith('  ✗') ? '#dc2626' : line.startsWith('  →') ? t.accent : t.textMuted }}>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Admin Screen ──────────────────────────────────────────────
function AdminScreen({ t, currentUser, notify }) {
  const [tab, setTab]           = useState('users'); // users | logins | inactivity
  const [users, setUsers]       = useState([]);
  const [stats, setStats]       = useState(null);
  const [logs, setLogs]         = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [inactivityResult, setInactivityResult] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [inactDays, setInactDays] = useState(90);
  const [inactRunning, setInactRunning] = useState(false);

  const loadData = () => {
    Promise.all([admin.listUsers(), admin.stats(), admin.enrichLogs()])
      .then(([u, s, l]) => { setUsers(u); setStats(s); setLogs(l); })
      .catch(e => notify(e.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const loadLoginHistory = () => {
    admin.loginHistory().then(setLoginHistory).catch(e => notify(e.message, 'error'));
  };

  useEffect(() => { if (tab === 'logins') loadLoginHistory(); }, [tab]);

  const updateRole = async (id, role) => {
    setUpdating(id);
    try {
      await admin.updateRole(id, role);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));
      notify(`Role updated to ${role}`, 'success');
    } catch (e) { notify(e.message, 'error'); }
    setUpdating(null);
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Permanently delete "${user.display_name}"?\n\nThis will:\n• Ban and soft-delete the account\n• Destroy all active sessions\n• Email the user a removal notice\n\nComic data is preserved for 30 days.`)) return;
    setDeleting(user.id);
    try {
      await admin.deleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      notify(`${user.display_name} deleted and notified`, 'success');
    } catch (e) { notify(e.message, 'error'); }
    setDeleting(null);
  };

  const runInactivity = async (dryRun) => {
    setInactRunning(true);
    setInactivityResult(null);
    try {
      const result = await admin.checkInactivity(dryRun, inactDays);
      setInactivityResult(result);
      notify(`${dryRun ? 'Preview' : 'Sent'}: ${result.warned} warning emails`, dryRun ? 'info' : 'success');
    } catch (e) { notify(e.message, 'error'); }
    setInactRunning(false);
  };

  const roleColor = (role) => ({
    admin:  { bg:'#c9a84c22', color:'#c9a84c' },
    banned: { bg:'#dc262622', color:'#dc2626' },
    user:   { bg:t.accentSoft, color:t.textMuted },
  }[role] || { bg:t.accentSoft, color:t.textMuted });

  return (
    <div className="fade-in" style={{ maxWidth:1000, margin:'0 auto', padding:'1.5rem 1rem' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:700, color:t.text, marginBottom:4 }}>Admin Panel</h1>
        <div style={{ fontSize:13, color:t.textMuted }}>Manage users, roles, logins, and site-wide tools</div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px, 1fr))', gap:10, marginBottom:24 }}>
          {[
            { label:'Total Users',     val: stats.total_users },
            { label:'Admins',          val: stats.admin_count },
            { label:'Banned',          val: stats.banned_count },
            { label:'Profiles',        val: stats.total_profiles },
            { label:'Comics',          val: stats.total_comics },
            { label:'Active Sessions', val: stats.active_sessions },
          ].map(s => (
            <div key={s.label} style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:10, padding:'12px 14px' }}>
              <div style={{ fontSize:22, fontWeight:700, color:t.text }}>{s.val}</div>
              <div style={{ fontSize:11, color:t.textMuted, marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:20, borderBottom:`1px solid ${t.border}`, paddingBottom:12 }}>
        {[
          { k:'users',      label:'👥 Users' },
          { k:'enrich',     label:'⚡ Data Enrichment' },
          { k:'logins',     label:'🔐 Login History' },
          { k:'inactivity', label:'⏰ Inactivity' },
        ].map(tb => (
          <button key={tb.k} onClick={() => setTab(tb.k)}
            style={{ background:tab===tb.k?t.accent:t.surface, border:`1px solid ${tab===tb.k?t.accent:t.border}`, borderRadius:8, padding:'7px 14px', color:tab===tb.k?'#fff':t.textMuted, fontSize:13, fontWeight:tab===tb.k?600:400 }}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* ── Users tab ── */}
      {tab === 'users' && (
        <Section t={t} label={`Users (${users.length})`}>
          {loading && <div style={{ textAlign:'center', padding:'2rem' }}><Spinner color={t.accent} /></div>}
          {!loading && (
            <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:t.surfaceAlt, borderBottom:`1px solid ${t.border}` }}>
                    {['ID','Name','Email','Provider','Role','Comics','Last Login','Actions'].map(h => (
                      <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:t.textMuted, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => {
                    const rc = roleColor(u.role);
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <tr key={u.id} style={{ borderBottom:`1px solid ${t.border}`, background: i%2===0?t.surface:t.bg }}>
                        <td style={{ padding:'8px 12px', color:t.textMuted, fontFamily:'monospace', fontSize:11 }}>{u.id}</td>
                        <td style={{ padding:'8px 12px', fontWeight:500, color:t.text }}>
                          {u.display_name}
                          {isSelf && <span style={{ fontSize:10, color:t.accent, marginLeft:6 }}>(you)</span>}
                        </td>
                        <td style={{ padding:'8px 12px', color:t.textMuted, fontSize:11 }}>{u.email}</td>
                        <td style={{ padding:'8px 12px' }}><span style={{ fontSize:11, color:t.textMuted, textTransform:'capitalize' }}>{u.oauth_provider}</span></td>
                        <td style={{ padding:'8px 12px' }}>
                          <span style={{ background:rc.bg, color:rc.color, fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:5, textTransform:'uppercase' }}>{u.role}</span>
                        </td>
                        <td style={{ padding:'8px 12px', color:t.textMuted, textAlign:'center' }}>{u.comic_count}</td>
                        <td style={{ padding:'8px 12px', color:t.textMuted, fontSize:11, whiteSpace:'nowrap' }}>{u.last_login?.slice(0,10) || '—'}</td>
                        <td style={{ padding:'8px 12px' }}>
                          {!isSelf && (
                            <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                              {u.role !== 'admin' && (
                                <button onClick={() => updateRole(u.id, 'admin')} disabled={!!updating}
                                  style={{ fontSize:11, padding:'3px 8px', background:'rgba(201,168,76,.15)', border:'1px solid rgba(201,168,76,.3)', borderRadius:5, color:'#c9a84c', cursor:'pointer' }}>
                                  Promote
                                </button>
                              )}
                              {u.role === 'admin' && (
                                <button onClick={() => updateRole(u.id, 'user')} disabled={!!updating}
                                  style={{ fontSize:11, padding:'3px 8px', background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:5, color:t.textMuted, cursor:'pointer' }}>
                                  Demote
                                </button>
                              )}
                              {u.role !== 'banned' && u.role !== 'admin' && (
                                <button onClick={() => updateRole(u.id, 'banned')} disabled={!!updating}
                                  style={{ fontSize:11, padding:'3px 8px', background:'rgba(220,38,38,.1)', border:'1px solid rgba(220,38,38,.25)', borderRadius:5, color:'#dc2626', cursor:'pointer' }}>
                                  Ban
                                </button>
                              )}
                              {u.role === 'banned' && (
                                <button onClick={() => updateRole(u.id, 'user')} disabled={!!updating}
                                  style={{ fontSize:11, padding:'3px 8px', background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:5, color:t.textMuted, cursor:'pointer' }}>
                                  Unban
                                </button>
                              )}
                              {u.role !== 'admin' && (
                                <button onClick={() => deleteUser(u)} disabled={deleting === u.id}
                                  style={{ fontSize:11, padding:'3px 8px', background:'rgba(220,38,38,.15)', border:'1px solid rgba(220,38,38,.35)', borderRadius:5, color:'#dc2626', cursor:'pointer', fontWeight:600 }}>
                                  {deleting === u.id ? '…' : 'Delete'}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* ── Enrichment tab ── */}
      {tab === 'enrich' && (
        <>
          <Section t={t} label="Data Enrichment">
            <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:16 }}>
              <div style={{ fontSize:13, color:t.textMuted, lineHeight:1.7, marginBottom:12 }}>
                Backfill missing data from Comic Vine, Google Books, and Open Library for all comics in the database.
              </div>
              <AdminEnrichmentRunner t={t} notify={notify} onComplete={(l) => setLogs(l)} />
            </div>
          </Section>
          <Section t={t} label={`Enrichment History (${logs.length})`}>
            {logs.length === 0
              ? <div style={{ fontSize:13, color:t.textMuted }}>No enrichment runs yet.</div>
              : (
                <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background:t.surfaceAlt, borderBottom:`1px solid ${t.border}` }}>
                        {['Date','By','Mode','Total','Updated','Skipped','Errors','Emailed','Duration'].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:t.textMuted }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l, i) => {
                        const duration = l.completed_at ? Math.round((new Date(l.completed_at) - new Date(l.started_at)) / 1000) : null;
                        return (
                          <tr key={l.id} style={{ borderBottom:`1px solid ${t.border}`, background:i%2===0?t.surface:t.bg }}>
                            <td style={{ padding:'7px 12px', color:t.textMuted, whiteSpace:'nowrap' }}>{l.started_at?.slice(0,16)}</td>
                            <td style={{ padding:'7px 12px', color:t.text }}>{l.started_by_name}</td>
                            <td style={{ padding:'7px 12px' }}>
                              <span style={{ fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:4, background:l.mode==='dry_run'?t.accentSoft:'#16a34a22', color:l.mode==='dry_run'?t.textMuted:'#16a34a' }}>
                                {l.mode === 'dry_run' ? 'DRY RUN' : 'LIVE'}
                              </span>
                            </td>
                            <td style={{ padding:'7px 12px', color:t.textMuted, textAlign:'center' }}>{l.total}</td>
                            <td style={{ padding:'7px 12px', color:'#16a34a', fontWeight:600, textAlign:'center' }}>{l.updated}</td>
                            <td style={{ padding:'7px 12px', color:t.textMuted, textAlign:'center' }}>{l.skipped}</td>
                            <td style={{ padding:'7px 12px', color:l.errors>0?'#dc2626':t.textMuted, textAlign:'center' }}>{l.errors}</td>
                            <td style={{ padding:'7px 12px', textAlign:'center' }}>{l.emailed ? <span style={{ color:'#16a34a' }}>✓</span> : '—'}</td>
                            <td style={{ padding:'7px 12px', color:t.textMuted }}>{duration !== null ? `${duration}s` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </Section>
        </>
      )}

      {/* ── Login History tab ── */}
      {tab === 'logins' && (
        <Section t={t} label="Recent Logins">
          <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, overflow:'hidden' }}>
            {loginHistory.length === 0
              ? <div style={{ padding:'2rem', textAlign:'center', color:t.textMuted }}>No login history yet.</div>
              : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:t.surfaceAlt, borderBottom:`1px solid ${t.border}` }}>
                      {['User','Provider','Date & Time','IP Address','User Agent'].map(h => (
                        <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:t.textMuted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.map((l, i) => (
                      <tr key={l.id} style={{ borderBottom:`1px solid ${t.border}`, background:i%2===0?t.surface:t.bg }}>
                        <td style={{ padding:'7px 12px', fontWeight:500, color:t.text }}>{l.display_name}</td>
                        <td style={{ padding:'7px 12px', color:t.textMuted, textTransform:'capitalize' }}>{l.provider}</td>
                        <td style={{ padding:'7px 12px', color:t.textMuted, whiteSpace:'nowrap' }}>{l.logged_in_at?.slice(0,16)}</td>
                        <td style={{ padding:'7px 12px', color:t.textMuted, fontFamily:'monospace', fontSize:11 }}>{l.ip_address || '—'}</td>
                        <td style={{ padding:'7px 12px', color:t.textMuted, fontSize:11, maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.user_agent || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </Section>
      )}

      {/* ── Inactivity tab ── */}
      {tab === 'inactivity' && (
        <Section t={t} label="Inactivity Management">
          <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:12, padding:16, display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ fontSize:13, color:t.textMuted, lineHeight:1.7 }}>
              Find users who haven't logged in for a set number of days and send them a warning email from <strong style={{ color:t.text }}>Admin@comics.tomory.net</strong>. Users who log in after receiving the warning are automatically cleared. Emails are not re-sent within 7 days.
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <label style={{ fontSize:13, color:t.text }}>Inactive for more than</label>
              <input type="number" value={inactDays} onChange={e => setInactDays(parseInt(e.target.value)||90)} min={7} max={365}
                style={{ width:70, background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:7, padding:'6px 10px', color:t.text, fontSize:13, outline:'none', textAlign:'center' }} />
              <label style={{ fontSize:13, color:t.text }}>days</label>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => runInactivity(true)} disabled={inactRunning}
                style={{ background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'9px 18px', color:t.textMuted, fontSize:13, fontWeight:500 }}>
                {inactRunning ? '…' : '🔍 Preview (no emails)'}
              </button>
              <button onClick={() => runInactivity(false)} disabled={inactRunning}
                style={{ background:t.accent, border:'none', borderRadius:8, padding:'9px 18px', color:'#fff', fontSize:13, fontWeight:600 }}>
                {inactRunning ? '…' : '📧 Send Warning Emails'}
              </button>
            </div>

            {inactivityResult && (
              <div style={{ background:t.surfaceAlt, borderRadius:10, padding:14 }}>
                <div style={{ fontSize:13, fontWeight:600, color:t.text, marginBottom:10 }}>
                  {inactivityResult.dry_run ? 'Preview — ' : ''}{inactivityResult.found} inactive users found · {inactivityResult.warned} {inactivityResult.dry_run ? 'would be' : ''} emailed
                </div>
                {inactivityResult.results.length > 0 && (
                  <div style={{ background:t.surface, border:`1px solid ${t.border}`, borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead>
                        <tr style={{ background:t.surfaceAlt, borderBottom:`1px solid ${t.border}` }}>
                          {['Name','Email','Last Login','Days Inactive','Email Sent'].map(h => (
                            <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:t.textMuted }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {inactivityResult.results.map((r, i) => (
                          <tr key={r.id} style={{ borderBottom:`1px solid ${t.border}`, background:i%2===0?t.surface:t.bg }}>
                            <td style={{ padding:'6px 12px', color:t.text }}>{r.name}</td>
                            <td style={{ padding:'6px 12px', color:t.textMuted, fontSize:11 }}>{r.email}</td>
                            <td style={{ padding:'6px 12px', color:t.textMuted }}>{r.last_login?.slice(0,10)}</td>
                            <td style={{ padding:'6px 12px', color: r.days_since > 180 ? '#dc2626' : '#d97706', fontWeight:600 }}>{r.days_since} days</td>
                            <td style={{ padding:'6px 12px' }}>{r.emailed ? <span style={{ color:'#16a34a' }}>✓ Sent</span> : <span style={{ color:t.textMuted }}>Preview only</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
}


// ── Admin Enrichment Runner — live DOM streaming, no React re-render per line ──
function AdminEnrichmentRunner({ t, notify, onComplete }) {
  const [running, setRunning]   = useState(false);
  const [progress, setProgress] = useState(null);
  const [options, setOptions]   = useState({ missing_only: true, dry_run: false });
  const logRef   = useRef(null);
  const abortRef = useRef(null);

  const appendLog = (line, color) => {
    if (!logRef.current) return;
    const el = document.createElement('div');
    el.textContent = line;
    el.style.color = color || 'rgba(160,155,150,0.8)';
    el.style.lineHeight = '1.7';
    logRef.current.appendChild(el);
    logRef.current.scrollTop = logRef.current.scrollHeight;
  };

  const run = async () => {
    setRunning(true);
    setProgress(null);
    if (logRef.current) logRef.current.innerHTML = '';
    abortRef.current = new AbortController();
    let total = 0, current = 0;

    try {
      const params = new URLSearchParams({
        missing_only: options.missing_only ? '1' : '0',
        dry_run:      options.dry_run      ? '1' : '0',
      });
      const res = await fetch(`/api/enrich?${params}`, {
        credentials: 'include',
        signal: abortRef.current.signal,
      });

      if (!res.ok) {
        const text = await res.text();
        appendLog(text.trim(), '#dc2626');
        setRunning(false);
        return;
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          const color = line.includes('✓')     ? '#16a34a'
                      : line.includes('✗')     ? '#dc2626'
                      : line.includes('→')     ? t.accent
                      : line.startsWith('===') ? t.text
                      : 'rgba(160,155,150,0.8)';
          appendLog(line, color);

          const foundMatch = line.match(/Found (\d+) comics/);
          if (foundMatch) total = parseInt(foundMatch[1]);

          const comicMatch = line.match(/\[(\d+)\/(\d+)\]\s+#\d+\s+—\s+(.+)/);
          if (comicMatch) {
            current = parseInt(comicMatch[1]);
            total   = parseInt(comicMatch[2]);
            setProgress({ current, total, comic: comicMatch[3].trim(), done: false });
          }
          if (line.includes('=== Complete ===')) {
            setProgress(p => p ? { ...p, done: true, current: total } : { current: total, total, comic: '', done: true });
          }
        }
      }

      notify(options.dry_run ? 'Dry run complete' : 'Enrichment complete ✓', 'success');
      admin.enrichLogs().then(l => { if (typeof onComplete === 'function') onComplete(l); }).catch(() => {});
    } catch (e) {
      if (e.name !== 'AbortError') {
        appendLog('Error: ' + e.message, '#dc2626');
        notify('Enrichment error: ' + e.message, 'error');
      }
    }
    setRunning(false);
  };

  const stop = () => { abortRef.current?.abort(); appendLog('\n— Stopped', t.textMuted); setRunning(false); };

  const pct = progress && progress.total > 0 ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
        {[
          { key:'missing_only', label:'Missing fields only' },
          { key:'dry_run',      label:'Dry run (preview only)' },
        ].map(o => (
          <label key={o.key} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:t.text, userSelect:'none' }}>
            <input type="checkbox" checked={options[o.key]} onChange={e => setOptions(p => ({ ...p, [o.key]: e.target.checked }))}
              style={{ width:15, height:15, accentColor:t.accent }} />
            {o.label}
          </label>
        ))}
      </div>

      <div style={{ display:'flex', gap:8 }}>
        <button onClick={run} disabled={running}
          style={{ background:t.accent, border:'none', borderRadius:8, padding:'10px 18px', color:'#fff', fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:8, opacity:running?0.6:1 }}>
          {running ? <><Spinner color="#fff" /> Running…</> : '▶ Run Enrichment'}
        </button>
        {running && (
          <button onClick={stop}
            style={{ background:'#dc262622', border:'1px solid #dc262655', borderRadius:8, padding:'10px 16px', color:'#dc2626', fontWeight:600, fontSize:14 }}>
            ✕ Stop
          </button>
        )}
      </div>

      {/* Live progress bar — updates via setProgress, smooth CSS transition */}
      {progress && (
        <div style={{ background:t.surfaceAlt, borderRadius:10, padding:'12px 14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
            <div style={{ fontSize:12, color:t.text, fontWeight:500, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginRight:12 }}>
              {progress.done ? '✓ All done' : progress.comic ? `Processing: ${progress.comic}` : 'Starting…'}
            </div>
            <div style={{ fontSize:12, color:t.textMuted, flexShrink:0 }}>
              {progress.current} / {progress.total} · {pct}%
            </div>
          </div>
          <div style={{ height:6, background:t.border, borderRadius:3, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background: progress.done ? '#16a34a' : t.accent, borderRadius:3, transition:'width 0.5s ease, background 0.3s ease' }} />
          </div>
          {!progress.done && running && (
            <div style={{ fontSize:11, color:t.textMuted, marginTop:5 }}>⟳ Fetching data from APIs…</div>
          )}
        </div>
      )}

      {/* Log output — DOM-appended directly, zero React re-renders per line */}
      <div ref={logRef} style={{ background:t.bg, border:`1px solid ${t.border}`, borderRadius:8, padding:'8px 12px', minHeight:48, maxHeight:280, overflowY:'auto', fontFamily:'monospace', fontSize:11, display: progress || running ? 'block' : 'none' }} />
    </div>
  );
}

function LookupResultCard({ t, comic, compact }) {
  return (
    <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
      {comic.cover_url
        ? <img src={comic.cover_url} alt={comic.title} style={{ width:compact?42:64, height:compact?58:88, objectFit:'cover', borderRadius:6, flexShrink:0 }} onError={e => e.target.style.display='none'} />
        : <div style={{ width:compact?42:64, height:compact?58:88, background:t.surfaceAlt, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:compact?18:24, flexShrink:0 }}>📖</div>}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:700, fontSize:compact?14:15 }}>{comic.title}</div>
        {comic.issue      && <div style={{ fontSize:13, color:t.accent, marginTop:1 }}>Issue #{comic.issue}</div>}
        {comic.publisher  && <div style={{ fontSize:12, color:t.textMuted }}>{comic.publisher}</div>}
        {comic.publish_year && <div style={{ fontSize:12, color:t.textMuted }}>{comic.publish_year}</div>}
        {comic.description && !compact && <div style={{ fontSize:12, color:t.textMuted, marginTop:4, lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{comic.description}</div>}
        <span style={{ background:t.accentSoft, color:t.accent, fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:5, marginTop:4, display:'inline-block' }}>{comic.source}</span>
      </div>
    </div>
  );
}

function ProfileSelector({ t, profiles, value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width:'100%', background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'9px 13px', color:t.text, fontSize:14, outline:'none' }}>
      {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );
}

function Section({ t, label, children, danger }) {
  return (
    <section style={{ marginBottom:28 }}>
      <h2 style={{ fontSize:13, fontWeight:600, color:danger?'#ef4444':t.textMuted, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>{label}</h2>
      {children}
    </section>
  );
}

function ActionBtn({ t, emoji, label, onClick, accent }) {
  return (
    <button onClick={onClick}
      style={{ flex:'1 1 120px', background:accent?t.accentSoft:t.surface, border:`1px solid ${accent?t.accent:t.border}`, borderRadius:12, padding:'12px 18px', color:accent?t.accent:t.text, fontWeight:600, fontSize:14, display:'flex', alignItems:'center', gap:10 }}>
      <span style={{ fontSize:20 }}>{emoji}</span>{label}
    </button>
  );
}

function BigBtn({ t, emoji, title, sub, onClick, accent }) {
  return (
    <button onClick={onClick}
      style={{ background:accent?t.accentSoft:t.surface, border:`1.5px solid ${accent?t.accent:t.border}`, borderRadius:14, padding:'18px 22px', display:'flex', alignItems:'center', gap:16, cursor:'pointer', textAlign:'left', width:'100%' }}>
      <span style={{ fontSize:30 }}>{emoji}</span>
      <div>
        <div style={{ fontWeight:600, fontSize:15, color:accent?t.accent:t.text }}>{title}</div>
        <div style={{ fontSize:13, color:t.textMuted, marginTop:2 }}>{sub}</div>
      </div>
    </button>
  );
}

function EmptyState({ t, icon, title, sub }) {
  return (
    <div style={{ textAlign:'center', padding:'4rem 1rem', color:t.textMuted }}>
      <div style={{ fontSize:44, marginBottom:12 }}>{icon}</div>
      <div style={{ fontSize:16, fontWeight:600, color:t.text, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:13 }}>{sub}</div>
    </div>
  );
}

function Spinner({ color }) {
  return <div style={{ width:20, height:20, border:`2.5px solid transparent`, borderTop:`2.5px solid ${color}`, borderRadius:'50%', animation:'spin 0.7s linear infinite', display:'inline-block' }} />;
}

function btnStyle(t) {
  return { background:t.surfaceAlt, border:`1px solid ${t.border}`, borderRadius:8, padding:'8px 14px', color:t.textMuted, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 };
}

function randomColor() {
  const colors = ['#a78bfa','#f59e0b','#10b981','#ef4444','#3b82f6','#06b6d4','#ec4899'];
  return colors[Math.floor(Math.random() * colors.length)];
}
